/*! 
 * v3.4
 */

/**
* Video Player jQuery Plugin
* (c) 2021 Eurobase
* author: gregm
* version: 3.4
* 
* Creates an HTML5 video player in a <div> element using the <video> tag and CSS.
* 
* Dependencies:
* 	- videoPlayer.css (+fonts/MaterialIcons...)
* 	- jQuery
* 	- jQuery UI
* 	- Material Design Icons
* 	- DASH player extension (dash.js)
* 	- portal-videolearning SessionController#service (for signed URLs)
* 
* Methods (public API):
* 	videoPlayer( options )
* 	videoPlayer( "currentTime" )
* 	videoPlayer( "duration" )
* 	videoPlayer( "jump2Position", position, play )
* 	videoPlayer( "pause" )
* 	videoPlayer( "updateAnnotations", json )
* 	videoPlayer( "updateBookmarks", json )
* 	videoPlayer( "updateChapters", json )
* 	videoPlayer( "updateTitles", json )
* 
* Usage:
* 
* 	Include the following in the head section of your HTML document...
* 
* 		<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
* 		<link href="videoPlayer.css" rel="stylesheet">
* 		<script src="https://ajax.googleapis.com/ajax/libs/jquery/3.2.1/jquery.min.js"></script>
* 		<script src="https://ajax.googleapis.com/ajax/libs/jqueryui/1.12.1/jquery-ui.min.js"></script>
* 		<script src="https://cdn.dashjs.org/latest/dash.all.min.js"></script>
* 		<script src="videoPlayer.js"></script>
* 
* 	Add a DIV element to the body of your HTML document...
* 
* 		<body>
* 			<div id="videoContainer"></div>
* 		</body>
* 
* 	Create the video player in the ready function...
* 
* 		$( document ).ready(function() {
* 			$("#videoContainer").videoPlayer({
* 				data : {
* 					previewUrl : "poster image url goes here",
* 					videoUrls : [{
* 						type : "video/mp4",
* 						url : "video url goes here"
* 					}]
* 				}
* 			});
* 		});
* 
*/
(function( $, window, document, undefined ) {

	$.error = window.console.error;
	
	/**
	* Default Settings
	*	
	* 	alwaysShowControls (true/false)
	* 		always show the video controls, if false then the controls are only shown when the mouse is over the player
	* 
	* 	autoPlay (true/false)
	* 		controls auto play attribute of video player, a value of true adds it
	*
	* 	debug (true/false)
	* 		log events to the console
	*
	* 	fullscreenResolution (integer or string)
	* 		the resolution the video should play at when in full screen mode
	*
	* 	initialShowControls (true/false)
	* 		initially show the video controls, if false then the controls are not shown initially
	*
	* 	iOS (true/false)
	* 		if the client is an iOS device
	*
	* 	noContent (string)
	* 		message to be displayed if no video content was provided
	*
	*	reportErrors (true/false)
	*		true: to report any native video element errors to the server
	*
	*	serviceUrl (string)
	*		string containing the URL of the video player service; to report errors, to sign the URLs
	*
	*	signVideoUrls (true/false/undefined)
	*		false: use signed cookies, true: use signed urls, undefined: neither signed cookies nor signed urls.
	*
	* 	startResolution (integer or string)
	* 		the resolution the video should start at, if the value is 0 (zero) then the highest resolution is used
	* 		and no video resolution menu is available, if the value computes to NaN then "auto" is used and ABR is activated
	*
	* 	textSubtitleLabels (object)
	* 		text for the subtitles menu labels { language code: label text, ... }
	*
	* 	titleChaptersButton (string)
	* 		title attribute for the chapter list button
	*
	* 	titleCloseAnnotationButton (string)
	* 		title attribute for the close annotation button
	*
	* 	titleExitFullscreenButton (string)
	* 		title attribute for the exit full screen button
	*
	* 	titleFullscreenButton (string)
	* 		title attribute for the full screen button
	*
	* 	titleMuteButton (string)
	* 		title attribute for the mute button
	*
	* 	titlePauseButton (string)
	* 		title attribute for the pause button
	*
	* 	titlePlayButton (string)
	* 		title attribute for the play button
	*
	* 	titleReplayButton (string)
	* 		title attribute for the replay button
	*
	* 	titleResolutionButton (string)
	* 		title attribute for the video resolution button
	*
	* 	titleResolutionMenu (string)
	* 		title text for the video resolution menu
	*
	* 	titleSubtitlesButton (string)
	* 		title attribute for the subtitles button
	*
	* 	titleThumbnailImage (string)
	* 		title attribute for the thumbnail images
	*
	* 	titleUnmuteButton (string)
	* 		title attribute for the unmute button
	*
	* 	typeNotSupported (string)
	* 		message to be displayed if the video type is not supported
	*
	* 	videoNotSupported (string)
	* 		message to be displayed if the video tag is not supported by the client
	*
	*	data (json)
	* 		JSON object with the video player meta data, this should be provided by the server
	*/
	var defaults = {
		alwaysShowControls : false,
		autoPlay : false,
		debug : false,
		fullscreenResolution : "720p",
		initialShowControls : false,
		iOS : false,
		noContent : "Sorry, no video content was provided.",
		reportErrors : false,
		serviceUrl : "../ws/service.xhtml",
		signVideoUrls : undefined,
		startResolution : "480p",
		textSubtitleLabels : undefined,
		titleChaptersButton : "Chapters",
		titleCloseAnnotationButton : "Close",
		titleExitFullscreenButton : "Exit Full Screen",
		titleFullscreenButton : "Full Screen",
		titleMuteButton : "Mute",
		titlePauseButton : "Pause",
		titlePlayButton : "Play",
		titleReplayButton : "Replay",
		titleResolutionButton : "Video Resolution",
		titleResolutionMenu : "Quality",
		titleSubtitlesButton : "Subtitles",
		titleThumbnailImage : "",
		titleUnmuteButton : "Unmute",
		typeNotSupported : "The video type(s) not supported by your web browser.",
		videoNotSupported : "Your browser does not support the video tag, consider upgrading to a web browser that supports HTML5 video.",
		data : {
			videoID : 0,					// internal video id
			userDataID : 0,					// internal user id
			classroomID : 0,				// internal classroom id
			organisationID : 0,				// internal organisation id
			lastWatchedPosition : 0,
			maxWatchedPosition : 0,			// maximum time a user has watched a given video, in seconds
			trackingInterval : 5,			// interval period to track play events, in seconds
			annotations : [],				// array of annotation objects { id, content, start, end, position ('tl' - top left, 'tr' - top right, 'bl' - bottom left, 'br' - bottom right) }
			bookmarks : [],					// array of bookmark objects { id, position (in seconds) }
			chapters : [],					// array of chapter objects { id, position (in seconds) }
			subtitleUrls : [],				// array of subtitle objects { language, url, flag }
			previewUrl : null,				// url for the poster image
			videoUrls : [],					// array of video objects { type, url }, used to handle different formats
			// external handlers
			onSetCurVideoPosition : undefined,		// send the current video position in intervals
			onVideoStarted : undefined,				// called when the play button is pressed
			onVideoEnded : undefined,				// called when the video ends
			onSelectThumbnail : undefined			// called when a thumb nail image is selected
		}
	};
	
	/**
	 * constants
	 */
	var MIME_TYPE_DASH = "application/dash+xml",			// MPEG-DASH
		MIME_TYPE_HLS = "application/vnd.apple.mpegurl",	// HTTP Live Streaming
		MIME_TYPE_MP4 = "video/mp4",						// H.264
		SUPPORTED_MIME_TYPES = [MIME_TYPE_MP4, MIME_TYPE_DASH, MIME_TYPE_HLS];
	
	var PLAY_BUTTON_JUMP_FACTOR = 10; // seconds
	
	
	var methods = {
		/**
		 * Create and initialize the video player.
		 * 
	     * @param options
	     * 		object to override the default settings (see above)
		 * @returns this jQuery object for chaining, the video container
		 */
	    init : function( options ) {

			// only create video player in a DIV
			return this.filter("div").each(function() {
				// override default settings
				this.settings = $.extend(true, {}, defaults, options);
				// used to control when the "timeupdate" event is logged, according to the tracking interval
				this.timeupdateInterval = null;
				// used to control when the video controls are hidden
				this.controlsInterval = null;
				
				// validate resolution settings
				var startResolution = parseInt(this.settings.startResolution);
				var fullscreenResolution = parseInt(this.settings.fullscreenResolution);
				if (isNaN(startResolution) || startResolution == 0 || (!isNaN(fullscreenResolution) && fullscreenResolution < startResolution)) {
					this.settings.fullscreenResolution = this.settings.startResolution;
				}
				
				var container = $(this);
				var settings = this.settings;
				
				// cleanup container first "Remove all child nodes..."
				container.empty();
				
				// add main styling class
				container.addClass("videoContainer");
				
				// check if there are videos to show
				if ($.isArray(settings.data.videoUrls) && settings.data.videoUrls.length) {
					var video = createVideoElement(settings);
					
					// check if the VIDEO tag is supported
					if (!isVideoSupported(video[0])) {
						container.append($("<p/>").addClass("failMessageBlock").text(settings.videoNotSupported));
					}
					
					// check if any of the video types are supported
					else if (!isTypeSupported(video[0], settings)) {
						container.append($("<p/>").addClass("failMessageBlock").text(settings.typeNotSupported));
					}
					
					// everything seems to be okay with the video, now add it and the controls to the video container
					else {
						// set class name for progress bar
						var classNameSeekBar = "seekBar optional";
						if(settings.data.mandatory == true) {
							classNameSeekBar = "seekBar mandatory";
						}
						
						var controls = createVideoControls(settings, classNameSeekBar);						
						var mainPlayBtn = createMainPlayBtn(settings);
						var replay = createVideoReplay(settings);
						
						container.append(video);
						container.append(controls);
						container.append(mainPlayBtn);
						container.append(replay);
						appendAnnotationOverlays(container, settings);
						addSubtitlesMenu(video, controls, settings);
						
						addEvents(container, video, controls);
						
						// if the video is MPEG DASH, attach the DASH player extension in order to play the manifest
						var mpd = getVideoUrl(settings.data.videoUrls, MIME_TYPE_DASH),
							dashOkay = !isIosDevice(settings) && (!isMac(settings) || !canPlayType(video[0], MIME_TYPE_HLS)); // DASH supported on Mac, but Safari has problems at the moment (HLS used in this case)
						if (dashOkay && isMediaSourceExtensionsSupported() && mpd) {
							// ...but first we need to get the signed URLs for all URLs in the manifest
							getDataFromManifest(settings, video, mpd.url)
							.done(function() {
								setupPlayerForDASH(settings, video, controls, mpd.url);
							})
							.fail(function(err) {
								$.error(err);
								container.empty().append($("<p/>").addClass("failMessageBlock").text(err));
							});
						}
						else if (mpd) {
							// we are not playing DASH, but we use the manifest to get the thumbnail data
							getDataFromManifest(settings, video, mpd.url)
							.done(function() {
								// read thumbnail data from Web VTT file
								loadThumbnailData(settings, video, controls, mpd.url, 0);
							})
							.fail(function(err) {
								$.error(err);
								container.empty().append($("<p/>").addClass("failMessageBlock").text(err));
							});
						}
					}
				}
				else {
					container.append($("<p/>").addClass("failMessageBlock").text(settings.noContent));
				}
			});
		
	    },
	    /**
	     * Get the current time position in the video stream.
	     * 
		 * @returns the current time position in a video in seconds
	     */
	    currentTime : function() {
	    	var video = this.children("video");
			
			return secondsToInt(video[0].currentTime);
		},
	    /**
	     * Get the duration of the video stream.
	     * 
		 * @returns the duration of a video in seconds
	     */
	    duration : function() {
	    	var video = this.children("video");
			
			return secondsToInt(video[0].duration);
		},
	    /**
	     * Pause a video player.
	     * 
		 * @returns this jQuery object for chaining, the video container
	     */
	    pause : function() {
			this.children("video").each(function() {
				if (this.pause) {
					this.pause();
				}
			});
			
			return this;
		},
	    /**
	     * Set a specific position in a video player.
	     * 
	     * @param position
	     * 		the position where the video should be set, in seconds
	     * @param play
	     * 		boolean indicating whether to start playing after setting the position
		 * @returns this jQuery object for chaining, the video container
	     */
	    jump2Position : function( position, play ) {
	    	var container = this;
			this.children("video").each(function() {
				if (setVideoPosition(container[0], this, position)) {
					if (play === true && this.play) {
						this.play();
					}
					else if (container.children(".replayBtn").is(":visible")) {
						container.children(".replayBtn").hide();
					}
				}
			});
			
			return this;
		},
	    /**
	     * Update the annotations in the player and in the array in settings.data.
	     * 
	     * @param data
	     * 		JSON object with the video player meta data, contains array of annotations
		 * @returns this jQuery object for chaining, the video container
	     */
	    updateAnnotations : function( data ) {
	    	var container = this;
	    	var video = this.children("video");
	    	var duration, time;
	    	if (video && video.length) {
	    		duration = video[0].duration;
	    		time = video[0].currentTime;
	    	}
			if (duration && time) {
				// reset annotations in the player, first remove...
				container.children("div.annotationOverlay").data("id", NaN).addClass("hide").children("div").html("");
				// ...then update
				updateAnnotationsInPlayer(container, time, duration, data.annotations);
				// update annotations in settings
				container[0].settings.data.annotations = data.annotations;
			}
			
			return this;
		},
	    /**
	     * Update the book mark positions in the progress bar.
	     * 
	     * @param data
	     * 		JSON object with the video player meta data, contains array of book mark positions
		 * @returns this jQuery object for chaining, the video container
	     */
	    updateBookmarks : function( data ) {
	    	var container = this;
	    	var video = this.children("video");
	    	var controls = this.children("div.videoControls");
			if (data.bookmarks && video && controls) {
				// removed book marks
				var oldBookmarks = diff(container[0].settings.data.bookmarks, data.bookmarks);
				for (var i=0; i < oldBookmarks.length; i++) {
					delBookmark(controls.find("div.seekControl"), oldBookmarks[i]);
				}
				
				// new book marks
				var newBookmarks = diff(data.bookmarks, container[0].settings.data.bookmarks);
				addMarkers(container, video, controls, newBookmarks, true);
				
				// update the book marks in settings
				container[0].settings.data.bookmarks = data.bookmarks;
			}
			
			return this;
		},
	    /**
	     * Update the chapter positions in the progress bar.
	     * 
	     * @param data
	     * 		JSON object with the video player meta data, contains array of chapter positions
		 * @returns this jQuery object for chaining, the video container
	     */
	    updateChapters : function( data ) {
	    	var container = this;
	    	var video = this.children("video");
	    	var controls = this.children("div.videoControls");
			if (data.chapters && video && controls) {
				// removed chapters
				var oldChapters = diff(container[0].settings.data.chapters, data.chapters);
				for (var i=0; i < oldChapters.length; i++) {
					delChapter(controls.find("div.seekControl"), oldChapters[i]);
				}
				
				// new chapters
				var newChapters = diff(data.chapters, container[0].settings.data.chapters);
				addMarkers(container, video, controls, newChapters, false);
				
				// update the chapters in settings
				container[0].settings.data.chapters = data.chapters;
			}
			
			return this;
		},
	    /**
	     * Update the video player controls' tooltips and menu titles, e.g. useful when there is a language change.
	     * 
	     * @param data
	     * 		JSON object with the video player meta data, contains an object with text values for all the title properties
		 * @returns this jQuery object for chaining, the video container
	     */
	    updateTitles : function( data ) {
	    	var container = this;
	    	var video = this.children("video");
	    	var controls = this.children("div.videoControls");
			if (video && controls) {
				if (data.titleChaptersButton) {
					$("#chapterSelBtn").attr("title", data.titleChaptersButton);
					container[0].settings.titleChaptersButton = data.titleChaptersButton;
				}
				if (data.titleCloseAnnotationButton) {
					container.children("div.annotationOverlay").children("span").attr("title", data.titleCloseAnnotationButton);
					container[0].settings.titleCloseAnnotationButton = data.titleCloseAnnotationButton;
				}
				if (data.titleExitFullscreenButton) {
					container[0].settings.titleExitFullscreenButton = data.titleExitFullscreenButton;
				}
				if (data.titleFullscreenButton) {
					$("#fullscreenBtn").attr("title", data.titleFullscreenButton);
					container[0].settings.titleFullscreenButton = data.titleFullscreenButton;
				}
				if (data.titleMuteButton) {
					if (!video[0].muted) $("#muteBtn").attr("title", data.titleMuteButton);
					container[0].settings.titleMuteButton = data.titleMuteButton;
				}
				if (data.titlePauseButton) {
					if (!video[0].paused) $("#mainPlayBtn").attr("title", data.titlePauseButton);
					container[0].settings.titlePauseButton = data.titlePauseButton;
				}
				if (data.titlePlayButton) {
					if (video[0].paused) $("#mainPlayBtn").attr("title", data.titlePlayButton);
					container[0].settings.titlePlayButton = data.titlePlayButton;
				}
				if (data.titleReplayButton) {
					$("#replayBtn").attr("title", data.titleReplayButton);
					container[0].settings.titleReplayButton = data.titleReplayButton;
				}
				if (data.titleResolutionButton) {
					$("#resolutionSelBtn").attr("title", data.titleResolutionButton);
					container[0].settings.titleResolutionButton = data.titleResolutionButton;
				}
				if (data.titleResolutionMenu) {
					controls.children("div.resolutionMenu").children(".menuTitle").text(data.titleResolutionMenu);
					container[0].settings.titleResolutionMenu = data.titleResolutionMenu;
				}
				if (data.titleSubtitlesButton) {
					$("#subtitlesBtn").attr("title", data.titleSubtitlesButton);
					container[0].settings.titleSubtitlesButton = data.titleSubtitlesButton;
				}
				if (data.titleThumbnailImage) {
					controls.children("div.thumbnailContainer").children(".thumbnailElem").attr("title", data.titleThumbnailImage);
					container[0].settings.titleThumbnailImage = data.titleThumbnailImage;
				}
				if (data.titleUnmuteButton) {
					if (video[0].muted) $("#muteBtn").attr("title", data.titleUnmuteButton);
					container[0].settings.titleUnmuteButton = data.titleUnmuteButton;
				}
				if (data.textSubtitleLabels) {
					controls.children("div.subtitlesMenu").find("li").each(function() {
						$(this).text( data.textSubtitleLabels[$(this).data("lang")] || $(this.data("lang")) );
					});
					container[0].settings.textSubtitleLabels = data.textSubtitleLabels;
				}
			}
			
			return this;
		}
		
	};
	
	/**
	 * Create and initialize a video player or call a video player method.
	 * 
     * @param method
     * 		the name of a method to be executed or
     * 		an object to override the default settings (see above) to create and initialize a video player or
     * 		nothing to create and initialize a video player with default settings
     *
	 * @returns this jQuery object for chaining, the video container
	 */
    $.fn.videoPlayer = function( method ) {
		if (methods[method]) {
			return methods[method].apply(this, Array.prototype.slice.call(arguments, 1));
		}
		else if (typeof method === "object" || !method) {
			return methods.init.apply(this, arguments);
		}
		else {
			$.error("error:  video player method \"" + method + "\" does not exist.");
		}
    };
    
    /**
     * Get basic browser information.
     * 
     * @returns the name of the client
     */
    function getBrowserInfo() {
    	var result;
    	
		if (window.navigator && window.navigator.userAgent) {
			var userAgent = window.navigator.userAgent.toLowerCase();
			/**** Edge ****/
			if (userAgent.indexOf("edge/") !== -1) {
				result = "edge";
			}
			/**** Opera ****/
			else if (userAgent.indexOf("opr/") !== -1 || userAgent.indexOf("opera/") !== -1 || userAgent.indexOf("opera ") !== -1) {
				result = "opera";
			}
			/**** Chrome ****/
			else if (userAgent.indexOf("chrome/") !== -1) {
				result = "chrome";
			}
			/**** Safari ****/
			else if (userAgent.indexOf("safari/") !== -1 && userAgent.indexOf("version/") !== -1) {
				result = "safari";
			}
			/**** Firefox ****/
			else if (userAgent.indexOf("firefox/") !== -1) {
				result = "firefox";
			}
			/**** Internet Explorer ****/
			else if (userAgent.indexOf("msie ") !== -1 || !!userAgent.match(/trident.*rv\:11\./)) {
				result = "msie";
			}
		}
		
		return result;
    }
    
    /**
     * Get the signed URLs for all URLs in the DASH manifest.
     * They are saved in a data attribute of the video element, "signedUrls".
     * Also, retrieve the data for each video representation (bandwidth, height, width) from the manifest
     * and store it in a data attribute of the video element, "representationData".
     * 
     * @param settings
     * 		current settings of player
     * @param video
	 * 		the video jQuery object
     * @param manifestUrl
     * 		the DASH manifest URL
     * @returns a promise to defer execution until this method completes
     */
	function getDataFromManifest(settings, video, manifestUrl) {
		var d = $.Deferred();
		
		// 1. get the manifest
		// 2. parse manifest for all URLs and video representation data
		// 3. for all URLs, get their signed counterparts
		// 4. (done) store signed URLs in an associative array (as a data attribute of the video element) for lookup later
		getDashManifest(settings, manifestUrl)
		.done(function(data) {
			var ndx, baseUrl = "", urls = [], representationData = [], representationDataObj, thumbnailFiles = [];
			
			if ((ndx = manifestUrl.lastIndexOf('/')) >= 0) {
				baseUrl = manifestUrl.substr(0, ndx + 1);
			}
			
			// all URLs
			$(data).find("AdaptationSet Representation").each(function() {
				urls.push(baseUrl + $(this).children("BaseURL").text());
			});
			
			// video representation data
			$(data).find("AdaptationSet Representation[mimeType='video/mp4']").each(function() {
				representationDataObj = {};
				representationDataObj.bandwidth = $(this).attr("bandwidth");
				representationDataObj.height = $(this).attr("height");
				representationDataObj.width = $(this).attr("width");
				representationData.push(representationDataObj);
			});
			
			// save the video representation data to a data attribute of the video element
			video.data("representationData", representationData);
			
			// thumbnail files
			$(data).find("AdaptationSet[mimeType='text/vtt'] Role[value='thumbnail']").siblings("Representation").each(function() {
				var thumbnailFile = {
					bandwidth : parseInt($(this).attr("bandwidth")),
					url : baseUrl + $(this).children("BaseURL").text()
				};
				
				thumbnailFiles.push(thumbnailFile);
			});
			
			// save the thumbnail files to a data attribute of the video element
			video.data("thumbnailFiles", thumbnailFiles);
			
			// get and store the signed URLs
			if (settings.signVideoUrls === true) {
				signUrls(settings, urls)
				.done(function(data) {
					if (data && data.urls) {
						var signedUrls = [], baseUrl, ndx;
						for (var i=0; i < data.urls.length; i++) {
							baseUrl = data.urls[i].url;
							if ((ndx = baseUrl.lastIndexOf('?')) >= 0) {
								baseUrl = baseUrl.substr(0, ndx);
							}
							signedUrls[baseUrl] = data.urls[i].url;
						}
						
						// save signed URLs to a data attribute of the video element
						video.data("signedUrls", signedUrls);
					}
					d.resolve();
				})
				.fail(function(jqXHR, textStatus, errorThrown) {
					d.reject("FAILURE signing URLs: "+textStatus+"/"+errorThrown+"/"+jqXHR.status+"/"+jqXHR.responseText+"\n\tDASH Manifest Urls: "+urls.toString());
				});
			}
			else {
				d.resolve(); // signed cookies used instead of signed URLs
			}
		})
		.fail(function(jqXHR, textStatus, errorThrown) {
			if ($.type(jqXHR) === "string") {
				d.reject("FAILURE getDataFromManifest: "+jqXHR+"\n\tDASH Manifest: "+manifestUrl);
			}
			else {
				d.reject("FAILURE getDataFromManifest: "+textStatus+"/"+errorThrown+"/"+jqXHR.status+"/"+jqXHR.responseText+"\n\tDASH Manifest: "+manifestUrl);
			}
		});
		
		return d.promise();
	}
	
    /**
     * Setup the video player for DASH.
	 * A new MediaPlayer is instantiated for the supplied videoElement.
     * 
     * @param settings
     * 		current settings of player
     * @param video
	 * 		the video jQuery object
	 * @param controls
	 * 		the video controls jQuery object
     * @param manifestUrl
     * 		the DASH manifest URL
     */
    function setupPlayerForDASH(settings, video, controls, manifestUrl) {
		var signedUrls = video.data("signedUrls") || [],
			representationData = video.data("representationData") || [],
			startQuality = parseInt(settings.startResolution),
			enableAutoSwitching = isNaN(startQuality),
			showResolutionMenu = isNaN(startQuality) || startQuality > 0,
			initialBitrate = NaN;
		
		// find initial bit rate to start at, must be obtained from the representation attributes in the manifest
		// since we cannot use our startResolution setting directly (dash library uses bandwidth to set the initial resolution)
		if (!isNaN(startQuality) && representationData.length) {
			for (var i = 0; i < representationData.length; i++) {
				initialBitrate = Math.ceil(representationData[i].bandwidth / 1000); // the initial bit rate in kbs
				if (startQuality <= 0 || representationData[i].height <= startQuality)
					break;
			}
		}
		
    	// create the dashjs media player, initialize it and attach the video element
		var dashPlayer = dashjs.MediaPlayer().create();
		
		// ...but first, extend player to use signed URLs (must be called before initialize)
		if (settings.signVideoUrls === true) {
			dashPlayer.extend("RequestModifier", function() {
				return {
					modifyRequestURL: function(url) {
						var signedUrl = signedUrls[url];
						if (signedUrl) {
							return signedUrl;
						}
						return url;
					}
				};
			}, true);
		}
		
		dashPlayer.initialize();

		if (settings.signVideoUrls === false) {
			dashPlayer.setXHRWithCredentialsForType(null, true); // set withCredentials for all types
		}
		
		// dash.js v2
		//dashPlayer.getDebug().setLogToBrowserConsole(settings.debug); // turns console logging on/off
		//dashPlayer.setFastSwitchEnabled(true); // when there is an up-switch in quality, move the change closer in the buffer instead of appending at the end
		//dashPlayer.setAutoSwitchQualityFor("video", enableAutoSwitching); // disable auto switching?
		
		// dash.js v3
		/*
		*/
		dashPlayer.updateSettings({
			debug: {
				logLevel: settings.debug ? dashjs.Debug.LOG_LEVEL_DEBUG : dashjs.Debug.LOG_LEVEL_NONE // turns console logging on/off
			},
			streaming: {
				fastSwitchEnabled: true, // when there is an up-switch in quality, move the change closer in the buffer instead of appending at the end
				abr: {
					autoSwitchBitrate: { video: enableAutoSwitching } // disable auto switching?
				}
			}
		});
		
		dashPlayer.setAutoPlay(false); // set to false, the autoplay property of the videoElement is preserved and dictates what happens here
		dashPlayer.attachView(video[0]); // attach the video element
		
		if (!isNaN(initialBitrate)) {
			// dash.js v2
			//dashPlayer.setInitialBitrateFor("video", initialBitrate);
			
			// dash.js v3
			/*
			*/
			dashPlayer.updateSettings({
				streaming: {
					abr: {
						initialBitrate: { video: initialBitrate }
					}
				}
			});
		}
		
		// event handlers
		dashPlayer.on(dashjs.MediaPlayer.events.ERROR, function(e) {
			$.error(e);
			reportError(settings, manifestUrl, "DASH", e);
		});
		dashPlayer.on(dashjs.MediaPlayer.events.STREAM_INITIALIZED, function(e) {
			if (settings.debug) {
				window.console.log(">>>>>>>>> MediaPlayer.events.STREAM_INITIALIZED <<<<<<<<<<");
				window.console.log(e);
			}
			
			var bitrateList = dashPlayer.getBitrateInfoListFor("video") || [];
			
			if (showResolutionMenu && bitrateList.length > 1) {
				var qualityIndex = isNaN(startQuality) ? NaN : dashPlayer.getQualityFor("video");
				var resolutionMenu = createResolutionMenu(representationData, bitrateList, qualityIndex, settings.titleResolutionMenu);
				resolutionMenu.find("li").each(function() {
					$(this).click(function() {
						if (!$(this).data("selected")) {
							$(this).siblings().removeClass("checked").data("selected", false);
							$(this).addClass("checked").data("selected", true);
							if (isNaN($(this).data("index"))) {
								// dash.js v2
								//dashPlayer.setAutoSwitchQualityFor("video", true); // Auto
								// dash.js v3
								/*
								*/
								dashPlayer.updateSettings({
									streaming: {
										abr: {
											autoSwitchBitrate: { video: true } // Auto
										}
									}
								});
							}
							else {
								// dash.js v2
								//if (dashPlayer.getAutoSwitchQualityFor("video")) {
								//	dashPlayer.setAutoSwitchQualityFor("video", false);
								//}
								// dash.js v3
								/*
								*/
								var dashSettings = dashPlayer.getSettings();
								if (dashSettings.streaming.abr.autoSwitchBitrate.video) {
									dashPlayer.updateSettings({
										streaming: {
											abr: {
												autoSwitchBitrate: { video: false } // turn Auto off
											}
										}
									});
								}
								
								dashPlayer.setQualityFor("video", $(this).data("index"));
							}
						}
					})
					.mouseover(function() {
						$(this).addClass("menuItemMouseOver");
					})
					.mouseleave(function() {
						$(this).removeClass("menuItemMouseOver");
					});
				});
				// attach the resolution menu and show the menu button
				controls.append(resolutionMenu).find("div.resolutionSelBtn").removeClass("hide");
			}
		});
		dashPlayer.on(dashjs.MediaPlayer.events.QUALITY_CHANGE_REQUESTED, function(e) {
			if (settings.debug) {
				window.console.log(">>>>>>>>> MediaPlayer.events.QUALITY_CHANGE_REQUESTED <<<<<<<<<<");
				window.console.log(e);
			}
			
			var bitrate = 0,
				bitrateIndex = e.newQuality,
				bitrateList = dashPlayer.getBitrateInfoListFor("video") || [];
			
			// get the bit rate
			if (bitrateIndex >= 0 && bitrateIndex < bitrateList.length) {
				bitrate = bitrateList[bitrateIndex].bitrate;
			}
			
			checkThumbnailData(settings, video, controls, dashPlayer.getSource(), bitrate);
		});
		dashPlayer.on(dashjs.MediaPlayer.events.PLAYBACK_METADATA_LOADED, function(e) {
			if (settings.debug) {
				window.console.log(">>>>>>>>> MediaPlayer.events.PLAYBACK_METADATA_LOADED <<<<<<<<<<");
				window.console.log(e);
			}
			
			// at this point, if we do not have thumbnail data then it needs to be loaded
			// usually the thumbnail data is already loaded because it gets loaded in the quality change requested event (above),
			// but this may not happen if there is only one resolution available or the initial bandwidth (startQuality)
			// did not warrant a change in quality
			if (!controls.data("thumbnails")) {
				var bitrate = 0,
				bitrateIndex = dashPlayer.getQualityFor("video"),
				bitrateList = dashPlayer.getBitrateInfoListFor("video") || [];
				
				// get the bit rate
				if (bitrateIndex >= 0 && bitrateIndex < bitrateList.length) {
					bitrate = bitrateList[bitrateIndex].bitrate;
				}
				
				checkThumbnailData(settings, video, controls, dashPlayer.getSource(), bitrate);
			}
		});
		
		// load manifest
		dashPlayer.attachSource(manifestUrl);
		dashPlayer.setTextDefaultEnabled(false); // turn subtitles off (otherwise text from the WebVTT file will be displayed)
    }
    
    /**
     * Check and load the thumbnail data.
     * 
     * @param settings
     * 		current settings of player
     * @param video
	 * 		the video jQuery object
     * @param controls
	 * 		the video controls jQuery object
     * @param mpd
	 * 		the URL of the DASH manifest file
     * @param bitrate
	 * 		used to select the Web VTT file if there is more than one (different resolutions)
     */
    function checkThumbnailData(settings, video, controls, mpd, bitrate) {
		var dataFileUrl,
			thumbnailData,
			thumbnailFiles;
	
		// do we already have the thumbnail data for the current bit rate
		if (thumbnailData = controls.data("thumbnails")) {
			if (thumbnailFiles = video.data("thumbnailFiles")) {
				if (thumbnailFiles.length == 1) return;
				
				var bandwidth;
				for (var i=0; i < thumbnailFiles.length; i++) {
					if (!dataFileUrl || thumbnailFiles[i].bandwidth <= bitrate) {
						dataFileUrl = thumbnailFiles[i].url;
						bandwidth = thumbnailFiles[i].bandwidth;
					}
				}
				
				if (bandwidth !== undefined && bandwidth == thumbnailData.currentBandwidth) return;
				
				thumbnailData.currentBandwidth = bandwidth;
				thumbnailData.thumbnails = [];
				controls.data("thumbnails", thumbnailData);
			}
		}
		
		// read thumbnail data from Web VTT file
		loadThumbnailData(settings, video, controls, mpd, bitrate, dataFileUrl);
    }
	
    /**
     * Log video events to the console if the debug setting is true.
     * 
     * @param settings
     * 		current settings of player
     * @param event
     * 		event object
     * @param time
     * 		position in the video, in seconds, where the event occurred
     * @param duration
     * 		length of the video, in seconds
     */
	function debug(settings, event, time, duration) {
		if (settings.debug) {
			if (window.console) {
				window.console.log("%s:  time = %f, duration = %f, percent = %d%%", event.type, time, duration, parseInt(time / duration * 100));
			}
		}
	}
	
	/**
	 * Test if the client is an iOS device.
	 * Feature detection (checking window.orientation) is now necessary because the iOS flag in settings
	 * is no longer sufficient - possible for an iPad to identify itself as a Mac (on the iPad:  Settings -> Safari -> Request Desktop Website -> All websites).  
	 * Therefore, checking the orientation property allows us to rule out a "real" Mac.
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @returns true if client is an iOS device
	 */
	function isIosDevice(settings) {
	    return settings.iOS && (typeof window.orientation !== "undefined");
	};
	
	/**
	 * Test if the client is a MacBook.
	 * Feature detection (checking window.orientation) is now necessary because the iOS flag in settings
	 * is no longer sufficient - possible for an iPad to identify itself as a Mac (on the iPad:  Settings -> Safari -> Request Desktop Website -> All websites).  
	 * Therefore, checking the orientation property allows us to rule out a "real" Mac.
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @returns true if client is a MacBook
	 */
	function isMac(settings) {
	    return settings.iOS && (typeof window.orientation === "undefined");
	};
	
	/**
	 * Check if the client video element can play a video type.
	 * 
	 * @param videoEl
	 * 		the actual video element, not the jQuery object
	 * @param type
	 * 		the video type to check
	 * @returns true if the video type can be played
	 */
	function canPlayType(videoEl, type) {
		if (videoEl && videoEl.canPlayType) {
			return videoEl.canPlayType(type).replace(/no/, '').length ? true : false;
		}
		
		return false;
	}
	
	/**
	 * Test if the video tag is supported by the client.
	 * 
	 * @param videoEl
	 * 		the actual video element, not the jQuery object
	 * @returns true / false
	 */
	function isVideoSupported(videoEl) {
		if (videoEl && videoEl.canPlayType) {
			return true;
		}
		
		return false;
	}
	
	/**
	 * Check if the video formats are supported by the client.
	 * 
	 * @param videoEl
	 * 		the actual video element, not the jQuery object
	 * @param settings
	 * 		object with current player settings
	 * @returns true / false
	 */
	function isTypeSupported(videoEl, settings) {
		var src = settings.data.videoUrls;
		
		if (isIosDevice(settings) && containsHLS(src)) {
			return true;
		}
		else if (isMac(settings) && containsHLS(src) && canPlayType(videoEl, MIME_TYPE_HLS)) {
			return true;
		}
		else if (isMediaSourceExtensionsSupported() && getVideoUrl(src, MIME_TYPE_DASH)) {
			return true;
		}
		else {
			for (var i=0; i < src.length; i++) {
				if (canPlayType(videoEl, src[i].type) && SUPPORTED_MIME_TYPES.indexOf(src[i].type.toLowerCase()) >= 0) {
					return true;
				}
			}
		}
		
		return false;
	}

	/**
	 * Check if the client supports MSE (can play MPEG DASH)
	 * 
	 * @returns true if the client can play DASH
	 */
	function isMediaSourceExtensionsSupported() {
		if (window.MediaSource) {
			return true;
		}
		return false;
	}
	
	/**
	 * Get one of the video sources that are to be added to the player by video type.
	 *  
	 * @param videos
	 * 		array of video sources to be added to the player (data.videoUrls)
	 * @param type
	 * 		mime type of video to look for
	 * @returns the video object from the data.videoUrls array found by type or null if it does not exist
	 */
	function getVideoUrl(videos, type) {
		for (var i=0; i < videos.length; i++) {
			if (videos[i].type.toLowerCase() == type) {
				return videos[i];
			}
		}
		return null;
	}
	
	/**
	 * Check if one of the video sources is HTTP Live Streaming.
	 *  
	 * @param videos
	 * 		array of video sources to be added to the player (data.videoUrls)
	 * @returns true / false
	 */
	function containsHLS(videos) {
		if (getVideoUrl(videos, MIME_TYPE_HLS)) {
			return true;
		}
		return false;
	}
	
	/**
	 * Create the video element.
	 * The source tags are only added to the video tag if we are not playing DASH,
	 * the client is iOS or there is no DASH manifest in the array of video objects.
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @returns the jQuery video object
	 */
	function createVideoElement(settings) {
		var video = $("<video/>");
		var addMP4 = true;
		var src = settings.data.videoUrls,		// array of video objects for the source elements
			vtt = settings.data.subtitleUrls,	// array of subtitle objects for the text track elements
			poster = settings.data.previewUrl,	// url for the poster image
			autoPlay = settings.autoPlay,		// add the auto play attribute, true or false
			macOS = isMac(settings),			// is client from a MacBook, true or false
			iOS = isIosDevice(settings);		// is client an iOS device, true or false
		
		if (video) {

			// we only need the poster for iOS devices
			if (poster && iOS) {
				video.attr("poster", poster);
			}
			
			// add autoplay attribute to video
			if (autoPlay) {
				video.attr("autoplay", "autoplay");
			}
			
			// fix for bug 3248 - Safari security error creating preview image from video
			if (macOS) {
				video.attr("crossorigin", settings.signVideoUrls === false ? "use-credentials" : "");
			}
			
			// add iOS specific attributes
			//if (iOS) {
			// TODO GM:  maybe should only be used for iPhone because on iPad it does not allow full screen in native player
			//	video.attr("playsinline", "playsinline"); // plays inline on iPhone
			//}
			
	    	// only add the "source" tags to the video element if we are not playing DASH,
	    	// otherwise it could cause problems in between video plays if the browser cache is cleared
	    	//	- the player attaches the source from the video element's "source" tag in some cases, e.g. especially after jumping ahead in a video
	    	// steps to reproduce problem:
	    	//	1. watch a video and skip ahead with mouse using the progress bar
	    	//	2. go back to topics page
	    	//	3. clear the browser cache
	    	//	4. watch same video again:  an unreported javascript error could occur, possible side effect is that no resolution button will be shown in player
			if (iOS || (macOS && canPlayType(video[0], MIME_TYPE_HLS)) || !isMediaSourceExtensionsSupported() || !getVideoUrl(src, MIME_TYPE_DASH)) {
				
				// do not include MP4 video if client is iOS or macOS Safari and HLS is present, e.g. because Safari plays MP4 by default
				if ((iOS || (macOS && canPlayType(video[0], MIME_TYPE_HLS))) && containsHLS(src)) {
					addMP4 = false;
				}
				
				for (var i=0; i < src.length; i++) {
					if (!addMP4 && src[i].type.toLowerCase() == MIME_TYPE_MP4)
						continue;
					
					// do not include DASH
					if (src[i].type.toLowerCase() != MIME_TYPE_DASH) {
						addSourceElement(video, src[i].url, src[i].type);
					}
				}
			}
			
			// add subtitles
			for (var i=0; i < vtt.length; i++) {
				addTextTrackElement(settings, video, vtt[i].url, vtt[i].lang);
			}
			
			//video.append($("<p/>").text("To view this video please enable JavaScript, and consider upgrading to a web browser that supports HTML5 video."));
		}
		
		return video;
	}
	
	/**
	 * Create a source element and add it to the video element.
	 * 
	 * @param elem
	 * 		the video jQuery object
	 * @param src
	 * 		the video url
	 * @param type
	 * 		the video type
	 */
	function addSourceElement(elem, src, type) {
		var source = $("<source/>").attr({
			"src" : src,
			"type" : type
		});
		
		elem.append(source);
	}
	
	/**
	 * Create a text track element and add it to the video element.
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @param elem
	 * 		the video jQuery object
	 * @param src
	 * 		the subtitle file url
	 * @param lang
	 * 		the subtitle language
	 */
	function addTextTrackElement(settings, elem, src, lang) {
//		var track = $("<track/>").attr({
//			"src" : src,
//			"kind" : "subtitles",
//			"srclang" : lang
//		});
//		
//		elem.append(track);
		
		$.ajax({
			url : src,
			xhrFields: {
				withCredentials: settings.signVideoUrls === false
		    },
		    cache : false,
			processData : false
		})
		.done(function(data) {
			var blob = new Blob([data], { type: "text/vtt" }),
				track = $("<track/>").attr({
				"src" : URL.createObjectURL(blob),
				"kind" : "subtitles",
				"srclang" : lang
			});
			elem.append(track);
		})
		.fail(function(jqXHR, textStatus, errorThrown) {
			var err = "FAILURE addTextTrackElement (src=" + src + ", lang=" + lang + "): ";
			if ($.type(jqXHR) === "string") {
				err += jqXHR;
			}
			else {
				err += textStatus + "/" + errorThrown + "/" + jqXHR.status + "/" + jqXHR.responseText;
			}
			
			if (settings.debug)
				console.log(err);
			
			reportError(settings, src, "WebVTT", err);
		});
	}
	
	/**
	 * Create the video controls:  seek bar, audio/fullscreen buttons, ...
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @param classNameSeekBar
	 * 		CSS class definitions for seek bar
	 * @returns the jQuery video controls object
	 */
	function createVideoControls(settings, classNameSeekBar) {
		var controls = $("<div/>")
			.addClass("videoControls")
			// seek control
			.append($("<div/>")
					.addClass("seekControl")
					.attr("id", "seekControl")
					.slider({
						animate : true,
						min : 0,
						max : 1,
						step : 0.01,
						range : "min",
						classes : {
							"ui-slider" : classNameSeekBar,
							"ui-slider-handle" : "seekHandle",
							"ui-slider-range-min" : "seekRangeMin"
						}
					})
			)
			// control panel
			.append($("<div/>")
					.addClass("controlPanel")
					.append($("<div/>")
							.addClass("currentTimeDuration unselectable")
							.append($("<span/>")
								.addClass("currentTime")
								.text("00:00")
							)
							.append($("<span/>")
								.addClass("sepTimeDuration")
								.text("/")
							)
							.append($("<span/>")
								.addClass("duration")
								.text("00:00")
							)
					)
					/*
					.append($("<div/>")							
							.addClass("playBtn unselectable")
							.attr("id", "playBtn")
							.attr("title", settings.titlePlayButton)
							.append($("<span/>")
									.addClass("material-icons iconSize24")
									.text("play_arrow")
									.css("vertical-align", "sub")
							)
					)
					*/					
					.append($("<div/>")
							.addClass("muteBtn unselectable")
							.attr("id", "muteBtn")
							.attr("title", settings.titleMuteButton)
							.append($("<span/>")
									.addClass("material-icons iconSize24")
									.text("volume_up")
									.css("vertical-align", "sub")
							)
					)
					.append($("<div/>")
							.addClass("volumeCtrl")
							.attr("id", "volumeCtrl")
							.data("lastVolumeLevel", 1)
							.slider({
								min : 0,
								max : 1,
								step : 0.01,
								range : "min",
								value : 1
							})
					)					
					.append($("<div/>")		// this button and the following are positioned float right - take care to add in reverse order
							.addClass("fullscreenBtn unselectable")
							.attr("id", "fullscreenBtn")
							.attr("title", settings.titleFullscreenButton)
							.append($("<span/>")
									.addClass("material-icons iconSize24")
									.text("fullscreen")
									.css("vertical-align", "sub")
							)
					)					
					.append($("<div/>")
							.addClass("resolutionSelBtn unselectable hide")
							.attr("id", "resolutionSelBtn")
							.attr("title", settings.titleResolutionButton)
							.append($("<span/>")
									.addClass("material-icons iconSize24")
									.text("video_label")
									.css("vertical-align", "sub")
							)
					)					
					.append($("<div/>")
							.addClass("chapterSelBtn unselectable hide")
							.attr("id", "chapterSelBtn")
							.attr("title", settings.titleChaptersButton)
							.append($("<span/>")
									.addClass("material-icons iconSize24")
									.text("list")
									.css("vertical-align", "sub")
							)
					)					
					.append($("<div/>")
							.addClass("subtitlesBtn unselectable hide")
							.attr("id", "subtitlesBtn")
							.attr("title", settings.titleSubtitlesButton)
							.append($("<span/>")
									.addClass("material-icons iconSize24")
									.text("chat")
									.css("vertical-align", "sub")
							)
					)					
			)
			// thumb nail container
			.append($("<div/>")
					.addClass("thumbnailContainer")
					.append($("<div/>")							
							.addClass("thumbnailElem")
							.attr("title", settings.titleThumbnailImage)
							.data("thumbnailId", NaN)														
							.append($("<canvas/>")
									.addClass("thumbnailImage")
							)
							.append($("<div/>")
									.addClass("thumbnailTime")
							)
					)
			);
		
		
		// remove full screen button if full screen is not supported by the client
		if (!isFullScreenAvailable()) {
			controls.find("#fullscreenBtn").remove();
		}
		
		// remove volume control if iOS device (which does not support setting the volume in javascript)
		if (isIosDevice(settings) === true) {
			controls.find("#muteBtn,#volumeCtrl").remove();
		}
		
		// show chapter button if there are chapters
		if (settings.data.chapters.length) {
			controls.find("#chapterSelBtn").removeClass("hide");
		}
		
		if (settings.alwaysShowControls === false && settings.initialShowControls === false) {
			controls.addClass("hide");
		}
		
		return controls;
	}
	
	/**
	 * Create the video resolution menu to be shown when the resolution selection button is clicked in the control bar.
	 * 
	 * @param representations
	 * 		the array of representations from the DASH manifest (the supported resolutions)
	 * 		contains 'bandwidth', 'height', 'width'
	 * @param bitrates
	 * 		the bit rate info array, created by the dash.js player from the DASH manifest (returned by getBitrateInfoListFor)
	 * 		contains 'bitrate', 'height', 'qualityIndex', 'width'
	 * @param qualityIndex
	 * 		the index of the initial resolution
	 * @param title
	 * 		the title text
	 * @returns the jQuery resolution menu object, in representation order
	 */
	function createResolutionMenu(representations, bitrates, qualityIndex, title) {
		var menu = $("<div/>")
			.addClass("resolutionMenu hide")
			.append($("<div/>")
				.addClass("menuTitle")
				.text(title)
			);
		
		var list = $("<ul/>");
		for (var i = 0; i < representations.length; i++) {
			for (var j = 0; j < bitrates.length; j++) {
				if (representations[i].height == bitrates[j].height && representations[i].bandwidth == bitrates[j].bitrate) {
					var selected = (bitrates[j].qualityIndex == qualityIndex),
						item = $("<li/>")
							.data( { selected : selected, index : bitrates[j].qualityIndex, resolution : bitrates[j].height } )
							.text( bitrates[j].height + "p" );
					if (selected) {
						item.addClass("checked");
					}
					list.append(item);
					break;
				}
			}
		}
		list.append($("<li/>")
			.addClass("auto" + (isNaN(qualityIndex) ? " checked" : ""))
			.data( { selected : (isNaN(qualityIndex) ? true : false), index : NaN, resolution : NaN } )
			.text( "Auto" )
		)
		
		menu.append(list);
		
		return menu;
	}
	
	/**
	 * Create the replay control
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @returns the jQuery replay control object
	 */
	function createVideoReplay(settings) {
		var replay = $("<div/>")
			.addClass("paperDP24 replayBtn")
			.attr("id", "replayBtn")
			.attr("title", settings.titleReplayButton)
			.append($("<span/>")
					.addClass("material-icons iconSize48")
					.text("replay")
			);
		
		return replay;
	}
	
	/**
	 * Create the main button control (play / pause),
	 * also the jump back and jump forward buttons
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @returns the jQuery play/pause button control object
	 */
	function createMainPlayBtn(settings) {
		var mainPlayBtnGrp = $("<div/>")
			.addClass("mainPlayBtnGroup")
			.append($("<div/>")
					.addClass("paperDP24 mainSecondaryPlayBtn notShown")
					.attr("id", "mainPlayBackBtn")
					.append($("<span/>")
							.addClass("material-icons iconSize48")
							.text("replay_10")
					)
			)
			.append($("<div/>")
					.addClass("paperDP24 mainPlayBtn")
					.attr("id", "mainPlayBtn")
					.attr("title", settings.titlePlayButton)
					.append($("<span/>")
							.addClass("material-icons iconSize48")
							.text("play_arrow")
					)
			)
			.append($("<div/>")
					.addClass("paperDP24 mainSecondaryPlayBtn notShown")
					.attr("id", "mainPlayForwardBtn")
					.append($("<span/>")
							.addClass("material-icons iconSize48")
							.text("forward_10")
					)
			);
		
		// show play button initially if we are not on auto play
		if (!settings.autoPlay) {
			mainPlayBtnGrp.css("display", "block");
		}
		
		// initially hide forward button if necessary, e.g. play position would be within 10 seconds of the maximum watched position for compulsory videos 
		if (settings.data.mandatory == true && settings.data.lastWatchedPosition + PLAY_BUTTON_JUMP_FACTOR > settings.data.maxWatchedPosition) {
			mainPlayBtnGrp.children("#mainPlayForwardBtn").addClass("hidden");
		}
		
		return mainPlayBtnGrp;
	}
	
	/**
	 * Adds the overlays for the video annotations to the player.
	 * 
	 * @param container
	 * 		the video container jQuery object
	 * @param settings
	 * 		object with current player settings
	 */
	function appendAnnotationOverlays(container, settings) {
		// centered overlay
		container.append($("<div/>")
				.addClass("annotationOverlay hide centered")
				.append($("<div/>")) // content container
				.append(createCloseBtnAnnotationOverlay(settings))
		);
		// top left overlay
		container.append($("<div/>")
				.addClass("annotationOverlay hide topLeft")
				.append($("<div/>")) // content container
				.append(createCloseBtnAnnotationOverlay(settings))
		);
		// top right overlay
		container.append($("<div/>")
				.addClass("annotationOverlay hide topRight")
				.append($("<div/>")) // content container
				.append(createCloseBtnAnnotationOverlay(settings))
		);
		// bottom left overlay
		container.append($("<div/>")
				.addClass("annotationOverlay hide bottomLeft")
				.append($("<div/>")) // content container
				.append(createCloseBtnAnnotationOverlay(settings))
		);
		// bottom right overlay
		container.append($("<div/>")
				.addClass("annotationOverlay hide bottomRight")
				.append($("<div/>")) // content container
				.append(createCloseBtnAnnotationOverlay(settings))
		);
	}
	
	/**
	 * Create the close button for the annotation overlays.
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @returns the jQuery close button control for the annotations
	 */
	function createCloseBtnAnnotationOverlay(settings) {
		var closeBtn = $("<span/>")
				.addClass("material-icons iconSize24")
				.attr("title", settings.titleCloseAnnotationButton)
				.text("highlight_off")
				.click(closeAnnotationOverlay);
		
		
		return closeBtn;
	}
	
	/**
	 * Function to close the annotation overlays when a user clicks the close icon.
	 * The 'hide' class is added to the overlay container and 'userClosed' property is set in the annotations array.
	 */
	function closeAnnotationOverlay(event) {
		event.stopPropagation();
		var currentId = $(this).parent().data("id");
		var settings = $(this).parent().parent()[0].settings;
		for (var i=0; i < settings.data.annotations.length; i++) {
			if (settings.data.annotations[i].id == currentId) {
				settings.data.annotations[i].userClosed = true;
				// re-center pause/play button if closing the centered annotation overlay
				if (settings.data.annotations[i].position.toLowerCase() == "ctr") {
					$(this).parent().siblings("div.mainPlayBtnGroup").removeClass("leftSide");
				}
				break;
			}
		}
		// reset overlay container (especially the 'id'
		// so it is redisplayed when the replay button is clicked
		// and it is at the beginning of the stream)
		$(this).parent().data("id", NaN).addClass("hide").children("div").html("");
	}
	
	/**
	 * Add the subtitles menu to be shown when the subtitles button is clicked in the control bar.
	 * 
	 * @param video
	 * 		the video jQuery object
	 * @param controls
	 * 		the video controls jQuery object
	 * @param settings
	 * 		object with current player settings
	 */
	function addSubtitlesMenu(video, controls, settings) {
		var subtitles = settings.data.subtitleUrls;	// array of subtitle objects
		if (subtitles.length) {
			if (subtitles.length > 1 || subtitles[0].flag == 0) { // add menu if player not in media pool where subtitles are automatic
				var subtitleLabels = settings.textSubtitleLabels || {},
					menu = $("<div/>")
						.addClass("subtitlesMenu hide");
				
				var list = $("<ul/>"), label, selected;
				for (var i=0; i < subtitles.length; i++) {
					label = subtitleLabels[subtitles[i].lang] || subtitles[i].lang;
					selected = (subtitles[i].flag != 0);
					var item = $("<li/>")
						.data( { selected : selected, lang : subtitles[i].lang } )
						.text( label );
					if (selected) {
						item.addClass("checked");
					}
					list.append(item);
				}
				list.prepend($("<li/>")
					.addClass("menuItemOff" + (!selected ? " checked" : ""))
					.data( { selected : !selected, lang : "off" } )
					.text( subtitleLabels["off"] || "Auto" )
				)
				list.find("li").each(function() {
					$(this).click(function() {
						if (!$(this).data("selected")) {
							$(this).siblings().removeClass("checked").data("selected", false);
							$(this).addClass("checked").data("selected", true);
							activateTextTrack(video, $(this).data("lang"));
						}
					})
					.mouseover(function() {
						$(this).addClass("menuItemMouseOver");
					})
					.mouseleave(function() {
						$(this).removeClass("menuItemMouseOver");
					});
				});
				
				menu.append(list);
				
				// attach the subtitles menu to the control panel and show the menu button
				controls.append(menu).find("div.subtitlesBtn").removeClass("hide");
			}
		}
	}
	
	/**
	 * Add the event handlers to the video and video controls.
	 * 
	 * @param container
	 * 		the video container jQuery object
	 * @param video
	 * 		the video jQuery object
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 */
	function addEvents(container, video, videoCtrls) {
		var containerEl = container[0],
			videoEl = video[0],
			settings = containerEl.settings,
			thumbnailContainer = videoCtrls.find("div.thumbnailContainer"),
			canSelectThumbnail = settings.data.onSelectThumbnail ? true : false,
			isStartPositionSet = false;
		
		// Container Events
		
		// disable context menu
		container.on("contextmenu", false);
		container.on("dragstart", false);
		
		function canHideVideoControls() {
			return (settings.alwaysShowControls === false && !videoCtrls.find("div.chapterList").is(":visible"));
		}
		
		function showVideoControls() {
			clearTimeout(containerEl.controlsInterval);
			$(this).children("div.annotationOverlay:visible").filter(".bottomLeft,.bottomRight").addClass("bottomVideoControls");
			videoCtrls.show();
			if (!videoEl.ended) {
				$(this).children("div.mainPlayBtnGroup").show();
			}
			containerEl.controlsInterval = setTimeout(function() {
				if (canHideVideoControls()) { // do not hide controls, if alwaysShowControls
					hideChapterList(videoCtrls, isFullScreen());
					hideResolutionMenu(videoCtrls);
					hideSubtitlesMenu(videoCtrls);
					if (videoEl.paused !== true) {
						videoCtrls.fadeOut("slow", function() {
							container.children("div.annotationOverlay:visible").removeClass("bottomVideoControls");
						});
					}
				}
				if (videoEl.paused !== true) {
					container.children("div.mainPlayBtnGroup").fadeOut("slow");
				}
			}, 5000);
		}
		
		function hideVideoControls() {
			clearTimeout(containerEl.controlsInterval);
			if (canHideVideoControls()) { // do not hide controls, if alwaysShowControls
				hideChapterList(videoCtrls, isFullScreen());
				hideResolutionMenu(videoCtrls);
				hideSubtitlesMenu(videoCtrls);
				if (videoEl.paused !== true || videoEl.currentTime <= 0.5) {
					videoCtrls.hide();
					$(this).children("div.annotationOverlay:visible").removeClass("bottomVideoControls");
				}
			}
			//if (videoEl.paused !== true) {
				$(this).children("div.mainPlayBtnGroup").hide();
			//}
		}
		
		function showThumbnails(seekBarCtrl, posX, curTime) {
			//if (videoCtrls.find("div.chapterList,div.resolutionMenu").is( ":visible" ))
			if (videoCtrls.find("div.resolutionMenu,div.subtitlesMenu").is( ":visible" ))
				 return; // no thumbnails if resolution menu is showing
			
			var thumbnailData = videoCtrls.data("thumbnails");
			if (!thumbnailData) return;
			if (!thumbnailData.thumbnails) return;
			if (!thumbnailData.thumbnails.length) return;
			if (!thumbnailContainer) return;
			
			var thumbnailElem = thumbnailContainer.find(".thumbnailElem"),
				thumbnailImage = thumbnailContainer.find(".thumbnailImage"),
				thumbnailTime = thumbnailContainer.find(".thumbnailTime"),
				seekBarOffset = seekBarCtrl.offset().left - videoCtrls.offset().left, // seek bar offset inside control panel
				signedUrls = video.data("signedUrls") || [];

			// Calculate time position given the mouse position
			if (!curTime) {
				curTime = Math.floor(videoEl.duration * posX / seekBarCtrl.outerWidth());
			}

			// Validate the current time
			if (isNaN(curTime) || curTime < 0 || curTime > videoEl.duration) return;
			
			// For mandatory videos, be careful not to show thumbnails where the user cannot skip ahead
			if (settings.data.mandatory && curTime > settings.data.maxWatchedPosition) {
				thumbnailContainer.css("display", "none");
				return;
			}
			
			// Get the thumbnail for the given time
			var thumbnail = getThumbnail(thumbnailData.thumbnails, curTime);
            if (!thumbnail) return;
            
            // Assign the thumbnail ID so it can be selected as a preview image for the video
            if (canSelectThumbnail) {
            	thumbnailElem.data("thumbnailId", thumbnail.id);
            }

            // Adjust posX variable for positioning thumbnail with regards to its viewport (control panel)
			posX += seekBarOffset;
			
			// Take into account thumbnail control so it is centered above the mouse position
			var elemWidth = parseInt(thumbnailElem.outerWidth()),
				elemHeight = parseInt(thumbnailElem.outerHeight());
			if (!isNaN(elemWidth)) {
				posX -= elemWidth / 2;
			}
			
			// Render thumbnail image inside a canvas, setting dimensions
			if (!thumbnailData.image || thumbnailData.image.src != thumbnail.url) {
				var img = new Image();
				img.onload = function() {
					thumbnailImage[0].width = elemWidth;
					thumbnailImage[0].height = elemHeight;
					var ctx = thumbnailImage[0].getContext("2d");
					ctx.drawImage(img, thumbnail.x * thumbnail.width, thumbnail.y * thumbnail.height, thumbnail.width, thumbnail.height, 0, 0, elemWidth, elemHeight);
					thumbnailData.image = img;
					videoCtrls.data("thumbnails", thumbnailData);
				};
				img.onerror = function() {
					thumbnailContainer.css("display", "none");
					$.error("Error loading thumbnail image: " + img.src);
					return;
				};
				img.src = signedUrls[thumbnail.url] || thumbnail.url;
			}
			else {
				var ctx = thumbnailImage[0].getContext("2d");
				ctx.drawImage(thumbnailData.image, thumbnail.x * thumbnail.width, thumbnail.y * thumbnail.height, thumbnail.width, thumbnail.height, 0, 0, elemWidth, elemHeight);
			}

			// Set thumbnail control position, but do not extend past the left and right sides of the seek bar
			if (thumbnailData.image) {
				thumbnailContainer.css({
					display: "block",
					left: (posX < seekBarOffset ? seekBarOffset : (posX + elemWidth > seekBarOffset + seekBarCtrl.outerWidth() ? seekBarOffset + seekBarCtrl.outerWidth() - elemWidth : posX)) + "px"
				});
			}
			
			// Update time label
			if (thumbnailTime) {
				thumbnailTime.text(secondsToTime(curTime));
			}
		}
		
		function createChapterList() {
			var chapters = settings.data.chapters,
				maxWatched = settings.data.maxWatchedPosition,
				toc = $("<div/>").addClass(isFullScreen() ? "chapterList fullscreen" : "chapterList");
			
			if (chapters) {
				var list = $("<ul/>");
				for (var i = 0; i < chapters.length; i++) {
					list.append($("<li/>")
							.data( { position : chapters[i].position } )
							.mouseover(function() {
								$(this).addClass("menuItemMouseOver");
							})
							.mouseleave(function() {
								$(this).removeClass("menuItemMouseOver");
							})
							.append($("<div/>")
									.append($("<div/>")
											.append($("<span/>")
													.addClass("time")
													.text( secondsToTime(secondsToInt(chapters[i].position)) )
											)
									)
									.append($("<div/>")
											.append($("<span/>")
													.text( chapters[i].name )
											)
									)
							)
							.click( gotoChapter )
					);
				}
				
				toc.append(list);
				
				// add to video controls
				videoCtrls.append(toc);
				
				// position and show chapter list
				var conH = isFullScreen() ? $(window).height() : container.height(); // in full screen mode use viewport height otherwise video container
					conH -= parseFloat(toc.css("bottom")); // chapter list container height = viewport height - bottom position
				var tocH = toc.outerHeight();
				
				toc.css("height", Math.min(conH,tocH));
			}
		}
		
		function updateChapterList() {
			var toc = videoCtrls.find("div.chapterList"),
				curPos = videoEl.currentTime >= videoEl.duration ? undefined : videoEl.currentTime,
				maxWatched = settings.data.maxWatchedPosition;
			if (toc.length) {
				var notSet = true,
					chEnd = Math.floor(videoEl.duration);
				$(toc.find("li").get().reverse()).each(function() {
					var chBeg = $(this).data("position");
					if (curPos !== undefined && curPos >= chBeg && notSet) {
						notSet = false;
						$(this).removeClass("played").addClass("playing");
					}
					else if (maxWatched >= chEnd) {
						$(this).removeClass("playing").addClass("played");
					}
					else {
						$(this).removeClass("playing");
					}
					chEnd = chBeg;
				});
			}
		}
		
		function gotoChapter() {
			var position = $(this).data("position");
			if (setVideoPosition(containerEl, videoEl, position)) {
				// if setting the video position is successful, hit play and close the chapter list
				videoEl.play();
				hideChapterList(videoCtrls, isFullScreen());
			}
		}
		
		// show the video controls when mouse hovers over the container		
		//if (settings.alwaysShowControls === false || settings.initialShowControls === true) {
			
			if (isIosDevice(settings) === false) { // do no mousemove event handling on ios, to prevent double tap problem.
				container.mousemove(showVideoControls).mouseleave(hideVideoControls);
			}
			else {
				container.on("touchstart", showVideoControls);
			}
		//}
		
		
		// onorientationchange - take care of chapter list, subtitles and resolution menu
		$(window).on("orientationchange", function() {
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			
			if (videoCtrls.find("div.chapterList").length) {
				hideChapterList(videoCtrls);
				createChapterList();
				updateChapterList();
			}
		});
		
		
		// onfullscreenchange - toggle full screen button
		// attention:  using material design icons
		$(document).on("fullscreenchange webkitfullscreenchange mozfullscreenchange MSFullscreenChange", function() {
			hideChapterList(videoCtrls);
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			thumbnailContainer.css("display", "none"); // make sure to hide thumbnail, e.g. in media pool
			
			if (isFullScreen()) {
				videoCtrls.addClass("videoControlsFullscreen").find("div.fullscreenBtn").attr("title", settings.titleExitFullscreenButton).find("span").text("fullscreen_exit");
				thumbnailContainer.find(".thumbnailElem").addClass("fullscreen");
				changeResolutionMenu(videoCtrls, settings.fullscreenResolution);
				if (settings.data.chapters.length) {
					// show the chapter button and list automatically when going full screen
					setTimeout(function() {
						//videoCtrls.find("div.chapterSelBtn").removeClass("hide");
						createChapterList();
						updateChapterList();
					}, 500); // time delay so render position is correct, really only necessary for Edge, IE 11, macOS Firefox
				}
			}
			else {
				videoCtrls.removeClass("videoControlsFullscreen").find("div.fullscreenBtn").attr("title", settings.titleFullscreenButton).find("span").text("fullscreen");
				thumbnailContainer.find(".thumbnailElem").removeClass("fullscreen");
				changeResolutionMenu(videoCtrls, settings.startResolution);
				//videoCtrls.find("div.chapterSelBtn").addClass("hide");
			}
			
			// if there are thumbnails, the thumbnail image size changes in full screen mode
			var thumbnailData = videoCtrls.data("thumbnails");
			if (thumbnailData) {
				thumbnailData.image = null; // this will reset the image
				videoCtrls.data("thumbnails", thumbnailData);
			}
		});
		
		
		// Video Events
		
		// Event:  error
		video.on("error", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			reportError(settings, this.currentSrc, "HTML5", e);
		});
		
		// Event:  loadedmetadata
		video.on("loadedmetadata", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			if (settings.debug) {
				if (window.console) {
					window.console.log("currentSrc:  %s", this.currentSrc);
				}
			}
			
			updateDuration(videoCtrls, duration);
			
			addMarkers(container, video, videoCtrls, settings.data.chapters, false);
			addMarkers(container, video, videoCtrls, settings.data.bookmarks, true);
			
			// initialize text tracks (subtitles)
			initTextTracks(settings, video);
			
			// set last watched position of video
			if (navigator.userAgent.indexOf("AppleWebKit") >= 0) {
				// WebKit browsers do not set the last position properly when it is at the end of the stream (for DASH - no frame shown) so set it near the end
				this.currentTime = Math.floor(settings.data.lastWatchedPosition) >= Math.floor(duration) ? Math.floor(duration) : settings.data.lastWatchedPosition;
			}
			else {
				this.currentTime = settings.data.lastWatchedPosition;
			}
			
			// show backward and forward buttons if not at the beginning
			if (this.currentTime > 0.5) {
				container.children("div.mainPlayBtnGroup").children("div.mainSecondaryPlayBtn").removeClass("notShown");
			}
		});
		
		// setting the last watched position in 'loadedmetadata' event is ignored in iOS devices so use the 'canplay' event
		if (isIosDevice(settings)) {
			// Event:  canplay
			video.on("canplay", function(e) {
				var duration = this.duration;
				var time = this.currentTime;
				
				debug(settings, e, time, duration);
				
				if (!isStartPositionSet) {
					// set last watched position of video
					this.currentTime = settings.data.lastWatchedPosition;
					isStartPositionSet = true;
				}
			});
		}
		
		// Event:  timeupdate
		video.on("timeupdate", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			// log event according to the tracking interval
			if (!containerEl.timeupdateInterval) {
				debug(settings, e, time, duration);
				
				// update maximum watched position locally
				if (settings.data.maxWatchedPosition < time) {
					settings.data.maxWatchedPosition = time;
				}
				
				if (settings.data.onSetCurVideoPosition && time > 0) {
					settings.data.onSetCurVideoPosition(settings.data, time, this); // call in intervals
				}
				
				updateChapterList();
				
				containerEl.timeupdateInterval = setTimeout(function() {
					containerEl.timeupdateInterval = null;
				}, settings.data.trackingInterval * 1000);
			}
			
			// update progress bar
			updateProgress(videoCtrls, settings, time, duration);
			
			// update annotations
			updateAnnotationsInPlayer(container, time, duration, settings.data.annotations);
		});
		
		// Event:  ended
		video.on("ended", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			if (settings.data.onSetCurVideoPosition) {
				settings.data.onSetCurVideoPosition(settings.data, time, this);
			}
			
			updateChapterList();
			
			// done in onSetCurVideoPosition, WebKit does not call 'ended' event in some cases (resolves bug 2570)
			//if (settings.data.onChapterChanged && settings.data.currentChapterID) {
			//	settings.data.onChapterChanged(settings.data.currentChapterID, undefined);
			//}
			
			// hide play/pause button
			container.children("div.mainPlayBtnGroup").hide();
			
			// show replay button
			if (container.children(".annotationOverlay.centered").hasClass("hide")) {
				container.children(".replayBtn").removeClass("leftSide").fadeIn(1000);
			}
			else {
				container.children(".replayBtn").addClass("leftSide").fadeIn(1000);
			}
			
			if (settings.data.onVideoEnded) {
				settings.data.onVideoEnded(settings.data);
			}
		});
		
		// Event:  pause
		video.on("pause", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			// reset play button
			// attention:  using material design icons
			//videoCtrls.find("div.playBtn").attr("title", settings.titlePlayButton).find("span").text("play_arrow");
			container.find("div.mainPlayBtn").attr("title", settings.titlePlayButton).find("span").text("play_arrow");
			
			if (settings.data.onSetCurVideoPosition) {
				settings.data.onSetCurVideoPosition(settings.data, time, this);
			}
			
			updateChapterList();
			
			// show pause/play button and control bar, taking care of any annotations that are shown
			//if (!this.ended) {
			//	container.children("div.annotationOverlay:visible").filter(".bottomLeft,.bottomRight").addClass("bottomVideoControls");
			//	container.children("div.mainPlayBtnGroup,div.videoControls").show();
			//}
			container.children("div.mainPlayBtnGroup").hide();
		});
		
		// Event:  play
		video.on("play", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			// change play button to a pause button
			// attention:  using material design icons
			//videoCtrls.find("div.playBtn").attr("title", settings.titlePauseButton).find("span").text("pause");
			container.find("div.mainPlayBtn").attr("title", settings.titlePauseButton).find("span").text("pause");

			if (settings.data.onVideoStarted) {
				settings.data.onVideoStarted(settings.data);				
			}
			if (settings.data.onSetCurVideoPosition) {
				settings.data.onSetCurVideoPosition(settings.data, time, this);
			}
			
			updateChapterList();
			
			// hide replay and pause/play buttons
			container.children(".replayBtn,div.mainPlayBtnGroup").fadeOut(1000);
			
			// hide control bar
			if (canHideVideoControls()) { // do not hide controls, if alwaysShowControls 
				container.children("div.videoControls").fadeOut(1000);
			}
		});
		
		// Event:  click
		video.on("click", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			onClickPlay(this);
		});
		
		// Event:  volumechange
		video.on("volumechange", function(e) {
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
			
			// change mute button to a unmute button
			// attention:  using material design icons
			if(this.muted) {
				videoCtrls.find("div.muteBtn").attr("title", settings.titleUnmuteButton).find("span").text("volume_off");
			} else {
				videoCtrls.find("div.muteBtn").attr("title", settings.titleMuteButton).find("span").text("volume_up");
			}
			
		});
		
		// Event:  prevent changing the playback rate, e.g. from within firebug
		video.on("ratechange", function(e) {
			this.playbackRate = 1;
			
			var duration = this.duration;
			var time = this.currentTime;
			
			debug(settings, e, time, duration);
		});
		
		// Video Controls Events
		
		// Event:  click on play button
		//videoCtrls.find("div.playBtn").on("click", function() {
		//	hideResolutionMenu(videoCtrls);
		//	onClickPlay(videoEl);
		//});
		
		// do stuff when the mouse leaves the control bar, e.g. hide the volume control
		videoCtrls.mouseleave(function() {
			$("#volumeCtrl").fadeOut();
		});
		
		// Event:  click on mute button
		videoCtrls.find("div.muteBtn").on("click", function() {
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			var volumeCtrl = videoCtrls.find(".volumeCtrl");
			if (volumeCtrl) {
				if (videoEl.muted && !isNaN(volumeCtrl.data("lastVolumeLevel"))) {
					volumeCtrl.slider("value", volumeCtrl.data("lastVolumeLevel"));
					setVolume(videoEl, volumeCtrl.data("lastVolumeLevel"));
				}
				else {
					volumeCtrl.data("lastVolumeLevel", parseFloat(videoEl.volume)).slider("value", 0);
					setVolume(videoEl, 0);
				}
			}
		});
		
		// Event:  mouse over mute button - show volume control
		videoCtrls.find("div.muteBtn").mouseover(function() {
			$("#volumeCtrl").fadeIn();
		});
		
		// Event:  slide the volume control
		videoCtrls.find(".volumeCtrl").on("slide", function(e, ui) {
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			setVolume(videoEl, ui.value);
		});
		
		// Event:  click on full screen button
		videoCtrls.find("div.fullscreenBtn").on("click", function() {
			thumbnailContainer.css("display", "none"); // make sure to hide thumbnail, e.g. in media pool
			toggleFullScreen(containerEl);
		});
		
		// Event:  click on chapters button
		videoCtrls.find("div.chapterSelBtn").on("click", function() {
			var toc = videoCtrls.find("div.chapterList");
			if (toc.length) {
				toc.remove();
			}
			else {
				thumbnailContainer.css("display", "none"); // make sure to hide thumbnail, e.g. in media pool
				hideResolutionMenu(videoCtrls);
				hideSubtitlesMenu(videoCtrls);
				
				createChapterList();
				updateChapterList();
			}
		});
		
		// Event:  click on resolution selection button
		videoCtrls.find("div.resolutionSelBtn").on("click", function() {
			var menu = videoCtrls.find("div.resolutionMenu");
			if (menu.hasClass("hide")) {
				thumbnailContainer.css("display", "none"); // make sure to hide thumbnail, e.g. in media pool
				hideChapterList(videoCtrls);
				hideSubtitlesMenu(videoCtrls);
				menu.removeClass("hide");
				// scroll checked element into view
				var sc = menu.find("ul");
				var se = menu.find("li.checked");
				sc.animate({
					scrollTop: se.offset().top - sc.offset().top + sc.scrollTop()
				});
			}
			else {
				menu.addClass("hide");
			}
			
			// adjust width of menu in case of vertical scrollbar
			var ul = menu.find("ul")[0];
			var scroll_h = ul.scrollHeight,
				client_h = ul.clientHeight,
				scroll_w = ul.scrollWidth,
				client_w = ul.clientWidth,
				padding_r;
			if (scroll_w > client_w) {
				padding_r = parseFloat(menu.find("li").css("paddingRight")) + (menu.width() - client_w);
				menu.find("li").css("paddingRight", padding_r + "px"); // add scrollbar width
			}
		});
		
		// Event:  click on subtitles button
		videoCtrls.find("div.subtitlesBtn").on("click", function() {
			var menu = videoCtrls.find("div.subtitlesMenu");
			if (menu.hasClass("hide")) {
				thumbnailContainer.css("display", "none"); // make sure to hide thumbnail, e.g. in media pool
				hideChapterList(videoCtrls);
				hideResolutionMenu(videoCtrls);
				// position aligned to right side of button and show menu
				var posX = $(this).parent().parent().outerWidth() - ($(this).position().left + $(this).width() + $(this).parent().position().left); // menu's parent width - button's position + button's width + menu's parent padding
				menu.css("right", posX).removeClass("hide");
			}
			else {
				menu.addClass("hide");
			}
		});
		
		// Event:  click on seek bar or drag handle - seek to a position in the video
		videoCtrls.find(".seekControl").on("slide", function(e, ui) {
			var newTime = videoEl.duration * ui.value;		// calculate the new time position in the video
			if (!setVideoPosition(containerEl, videoEl, newTime)) {
				return false;
			}
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			if (container.children(".replayBtn").is(":visible")) {
				container.children(".replayBtn").hide();
				container.children("div.mainPlayBtnGroup").show();
			}
			
			// Show the thumbnails as we slide along
			var posX = e.pageX - $(this).offset().left;	// position of mouse
			if (posX < 0)
				posX = 0;
			else if (posX > $(this).offset().left + $(this).outerWidth())
				posX = $(this).offset().left + $(this).outerWidth();
			showThumbnails($(this), posX, newTime);
		});
		
		videoCtrls.find(".seekControl").on("slidestart", function(e, ui) {
			$(this).data("sliding", true);
		});
		
		videoCtrls.find(".seekControl").on("slidestop", function(e, ui) {
			$(this).data("sliding", false);
			if (thumbnailContainer && !canSelectThumbnail) {
				thumbnailContainer.css("display", "none");
			}
		});
		
		// Event:  mousemove over seek bar - show thumbnails
		videoCtrls.find(".seekControl").on("mousemove touchmove", function(e) {
			var posX = e.pageX - $(this).offset().left; // mouse position in seek bar
			
			// Ignore if slider handle is being dragged
			if (!$(this).data("sliding")) {
				showThumbnails($(this), posX);
			}
		});
		
		// Event:  mouseover on progress bar
		//videoCtrls.find(".seekControl").on("mouseover touchstart", function(e) {
		//	$(this).addClass("active"); // increases the thickness of the progress bar
		//});
		
		// Event:  mouseout on seek bar - hide thumbnails
		videoCtrls.find(".seekControl").on("mouseout touchcancel touchend", function(e) {
			// Ignore if slider handle is being dragged
			if (!$(this).data("sliding")) {
				if (thumbnailContainer && !canSelectThumbnail) {
					thumbnailContainer.css("display", "none");
				}
			}
		});
		
		// Event:  click thumbnail image
		videoCtrls.find(".thumbnailElem").on("click", function() {
			if (!isNaN($(this).data("thumbnailId"))) {
				if (settings.data.onSelectThumbnail) {
					settings.data.onSelectThumbnail($(this).data("thumbnailId"));
				}
			}
			thumbnailContainer.css("display", "none");
		});
		
		// Event:  click on pause/play button
		container.find("#mainPlayBtn").on("click", function() {
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			onClickPlay(videoEl);
		});
		
		// Event:  click on go back button
		container.find("#mainPlayBackBtn").on("click", function() {
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			
			var val = videoEl.currentTime - PLAY_BUTTON_JUMP_FACTOR;
			setVideoPosition(containerEl, videoEl, Math.max(0, val));
		});
		
		// Event:  click on go forward button
		container.find("#mainPlayForwardBtn").on("click", function() {
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			
			var val = videoEl.currentTime + PLAY_BUTTON_JUMP_FACTOR;
			setVideoPosition(containerEl, videoEl, Math.min(videoEl.duration, val));
		});
		
		// Event:  click on replay button
		container.children("div.replayBtn").on("click", function() {
			// reset video annotations
			if (settings.data.annotations) {
				for (var i=0; i < settings.data.annotations.length; i++) {
					if (settings.data.annotations[i].userClosed) {
						settings.data.annotations[i].userClosed = false;
					}
				}
			}
			// jump to the beginning of video
			setVideoPosition(containerEl, videoEl, 0);
			hideChapterList(videoCtrls, isFullScreen());
			hideResolutionMenu(videoCtrls);
			hideSubtitlesMenu(videoCtrls);
			onClickPlay(videoEl);
		});
		
		// Event:  click on an annotation
		container.children("div.annotationOverlay").on("click", function() {
			// pause the video
			if (videoEl && videoEl.pause) {
				videoEl.pause();
			}
		});
		
	}
	
	/**
	 * Change the video resolution via the resolution menu. The click event is triggered on the menu item
	 * with the closest match to the desired resolution.
	 * 
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 * @param resolution
	 * 		the new resolution, must be non-zero
	 */
	function changeResolutionMenu(videoCtrls, resolution) {
		var newResolution = parseInt(resolution);
		if (videoCtrls && !videoCtrls.find("div.resolutionSelBtn").hasClass("hide") && (isNaN(newResolution) || newResolution > 0)) {
			videoCtrls.find("div.resolutionMenu li").each(function() {
				var resolution = $(this).data("resolution");
				if (isNaN(resolution) && isNaN(newResolution)) {
					$(this).click();
					return false;
				}
				else if (!isNaN(resolution) && !isNaN(newResolution) && resolution <= newResolution) {
					$(this).click();
					return false;
				}
			});
		}
	}
	
	/**
	 * Close the video resolution selection menu.
	 * 
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 */
	function hideResolutionMenu(videoCtrls) {
		if (videoCtrls) {
			videoCtrls.find("div.resolutionMenu").addClass("hide");
		}
	}
	
	/**
	 * Close the subtitles menu.
	 * 
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 */
	function hideSubtitlesMenu(videoCtrls) {
		if (videoCtrls) {
			videoCtrls.find("div.subtitlesMenu").addClass("hide");
		}
	}
	
	/**
	 * Close the chapter selection list.
	 * 
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 * @param show
	 * 		if a false value, hide the chapter list
	 */
	function hideChapterList(videoCtrls, show) {
		if (videoCtrls && !show) {
			videoCtrls.find("div.chapterList").remove();
		}
	}
	
	/**
	 * Activate/deactivate text tracks (subtitles).
	 * 
	 * @param video
	 * 		the video jQuery object
	 * @param lang
	 * 		the text track language to activate or deactivate if no match is found
	 */
	function activateTextTrack(video, lang) {
		// problem switching languages after the first time without turning off
		// workaround:  first, turn off all and then activate selected language with a timeout
		video.children("track").each(function() {
			var track = this.track;
			if (track.kind == "subtitles" && track.language && track.language.length) {
				track["default"] = false; // why?  default is a key work in some environments
				track.mode = "hidden";
			}
		});
		
		if (lang != "off") {
			setTimeout(function() {
				video.children("track").each(function() {
					var track = this.track;
					if (track.kind == "subtitles" && track.language && track.language.length) {
						if (track.language == lang) {
							track["default"] = true; // why?  default is a key work in some environments
							track.mode = "showing";
						}
						else {
							track["default"] = false; // why?  default is a key work in some environments
							track.mode = "hidden";
						}
					}
				});
			}, 500);
		}
	}
	
	/**
	 * Initialize text tracks for subtitles.
	 * 
	 * @param settings
	 * 		object with current player settings
	 * @param video
	 * 		the video jQuery object
	 */
	function initTextTracks(settings, video) {
		// first, determine if a text track for a given language is on by default
		var subtitles = settings.data.subtitleUrls, lang;
		for (var i=0; i < subtitles.length; i++) {
			if (subtitles[i].flag != 0) {
				lang = subtitles[i].lang;
				break;
			}
		}
		
		// initialize video text tracks
		var textTracks = video[0].textTracks, track;
		if (textTracks) {
			for (var i=0; i < textTracks.length; i++) {
				track = textTracks[i];
				if (track.kind == "subtitles" && track.language && track.language.length) {
					track["default"] = false; // why?  default is a key work in some environments
					track.mode = "hidden";
					track.addEventListener("cuechange", onCueChange);
				}
				else {
					// disable all other tracks, e.g. thumbnail file which is also WebVTT
					track["default"] = false; // why?  default is a key work in some environments
					track.mode = "disabled";
				}
			}
		}
		
		// text track on by default?, e.g. in media pool
		if (lang) {
			activateTextTrack(video, lang);
		}
	}
	
	/**
	 * Event Handler:  'cuechange'
	 * 
	 * Used to vertically position the subtitle cues.
	 */
	function onCueChange() {
		if (this.activeCues) {
			for (var i=0; i < this.activeCues.length; i++) {
				this.activeCues[i].snapToLines = false; // position as percentage of video height
				this.activeCues[i].line = 90; // percent
			}
		}
	}
	
	/**
	 * Update the video duration time that is displayed in the control panel.
	 * 
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 * @param duration
	 * 		duration of the video, in seconds
	 */
	function updateDuration(videoCtrls, duration) {
		if (videoCtrls) {
			videoCtrls.find("span.duration").text(secondsToTime(secondsToInt(duration)));
		}
	}
	
	/**
	 * Update the video annotations, showing or hiding when appropriate.
	 * 
	 * @param container
	 * 		the video container jQuery object
	 * @param time
	 * 		current time, in seconds
	 * @param duration
	 * 		duration of the video, in seconds
	 * @param annotations
	 * 		array of video annotation objects { id, content, start, end, position }
	 * @returns
	 */
	function updateAnnotationsInPlayer(container, time, duration, annotations) {
		var duration = secondsToInt(duration),
			annotations = annotations || [];
		
		for (var i=0; i < annotations.length; i++) {
			
			if (annotations[i].userClosed)
				continue; // annotation was closed by user, ignore
			
			var start = annotations[i].start || 0,
				end = annotations[i].end || duration,
				position = annotations[i].position + "" || "",
				checkBottomPosition = false,
				checkMainPlayBtnGroup = false,
				controlsVisible = container.children("div.videoControls").is(":visible");
			
			switch (position.toLowerCase()) {
			case "ctr": // centered vertically and horizontally
				position = ".centered";
				checkMainPlayBtnGroup = true;
				break;
			case "tl": // top left
				position = ".topLeft";
				break;
			case "bl": // bottom left
				position = ".bottomLeft";
				checkBottomPosition = true;
				break;
			case "br": // bottom right
				position = ".bottomRight";
				checkBottomPosition = true;
				break;
			default: // top right
				position = ".topRight";
			}
			
			container.children("div.annotationOverlay" + position).each(function() {
				if (checkBottomPosition) {
					if (controlsVisible)
						$(this).addClass("bottomVideoControls");
					else
						$(this).removeClass("bottomVideoControls");
				}
				
				if ((time < end || end >= duration) && time >= start) {
					// activate annotation
					if ($(this).data("id") != annotations[i].id) {
						$(this).data("id", annotations[i].id).removeClass("hide").children("div").html(annotations[i].content);
						if (checkMainPlayBtnGroup) {
							container.children("div.mainPlayBtnGroup").addClass("leftSide");
						}
					}
				}
				else {
					// deactivate annotation
					if ($(this).data("id") == annotations[i].id) {
						$(this).data("id", NaN).addClass("hide").children("div").html("");
						if (checkMainPlayBtnGroup) {
							container.children("div.mainPlayBtnGroup").removeClass("leftSide");
						}
					}
				}
			});
		}
	}
	
	/**
	 * Update the video progress in the progress bar and the current time in the control panel.
	 * 
	 * @param videoCtrls
	 * 		the video controls jQuery object
	 * @param settings
	 * 		object with current player settings
	 * @param time
	 * 		current time, in seconds
	 * @param duration
	 * 		duration of the video, in seconds
	 */
	function updateProgress(videoCtrls, settings, time, duration) {
		if (videoCtrls) {
			videoCtrls.find("span.currentTime").text(secondsToTime(time >= duration ? secondsToInt(duration) : time));
			videoCtrls.find(".seekControl").slider("value", parseInt(time / duration * 100) / 100);
			
			// make sure backward and forward buttons are shown if not starting at the beginning
			if (time > 0.5) {
				$("#mainPlayBackBtn").removeClass("notShown");
				$("#mainPlayForwardBtn").removeClass("notShown");
			}
			
			// hide the play forward button for mandatory videos
			// if it causes the position to jump past the maximum watched position
			if (settings.data.mandatory == true) {
				// if video has not been watched through to completion at least once
				if (Math.floor(settings.data.maxWatchedPosition) < Math.floor(duration)) {
					if (time + PLAY_BUTTON_JUMP_FACTOR > settings.data.maxWatchedPosition) {
						$("#mainPlayForwardBtn").addClass("hidden");
					}
					else {
						$("#mainPlayForwardBtn").removeClass("hidden");
					}
				}
				else if ($("#mainPlayForwardBtn").hasClass("hidden")) {
					$("#mainPlayForwardBtn").removeClass("hidden");
				}
			}
		}
	}
	
	/**
	 * Play or pause the video.
	 * 
	 * @param videoEl
	 * 		the actual video element, not the jQuery object
	 */
	function onClickPlay(videoEl) {
		if (videoEl) {
			if (videoEl.paused) {
				if (videoEl.play) videoEl.play();
			}
			else {
				if (videoEl.pause) videoEl.pause();
			}
		}
	}
	
	/**
	 * Set the player volume and synchronize controls (mute button) accordingly.
	 * 
	 * @param videoEl
	 * 		the actual video element, not the jQuery object
	 * @param value
	 * 		volume, value between 0 and 1 (inclusive)
	 */
	function setVolume(videoEl, value) {
		if (videoEl) {
			videoEl.volume = value;
			videoEl.muted = (videoEl.volume === 0);
		}
	}
	
	/**
	 * Set the current video position, must be between zero and the maxWatchedPosition value, otherwise it is not set.
	 * Users are not allowed to skip ahead in a mandatory video until they have watched it at least once.
	 * In an optional video the user can skip ahead!
	 * 
	 * @param videoContainerEl
	 * 		the video container element, not the jQuery object
	 * @param videoEl
	 * 		the actual video element, not the jQuery object
	 * @param position
	 * 		the position where the video should be set, in seconds
	 */
	function setVideoPosition(videoContainerEl, videoEl, position) {
		if (videoContainerEl && videoEl && videoEl.duration && $.isNumeric(position)) {
			if (position >= 0) {
				// check if video is mandatory
				if (videoContainerEl.settings.data.mandatory == true) {
					if (position <= videoContainerEl.settings.data.maxWatchedPosition) {
						resetTimeupdateInterval(videoContainerEl); // reset tracking interval
						videoEl.currentTime = position;
						if (videoContainerEl.settings.data.onSetCurVideoPosition) {
							videoContainerEl.settings.data.onSetCurVideoPosition(videoContainerEl.settings.data, position, videoEl);
						}
					}
					else {
						return false;
					}
				}
				else {
					resetTimeupdateInterval(videoContainerEl); // reset tracking interval
					videoEl.currentTime = position;
					if (videoContainerEl.settings.data.onSetCurVideoPosition) {
						videoContainerEl.settings.data.onSetCurVideoPosition(videoContainerEl.settings.data, position, videoEl);
					}
				}												
			}
		}
		
		return true;
	}
	
	/**
	 * Reset the tracking interval, generally when the video play position is set manually by a user.
	 *  
	 * @param videoContainerEl
	 * 		the video container element, not the jQuery object
	 */
	function resetTimeupdateInterval(videoContainerEl) {
		if (videoContainerEl && videoContainerEl.timeupdateInterval) {
			clearTimeout(videoContainerEl.timeupdateInterval);
			videoContainerEl.timeupdateInterval = null;
		}
	}
	
	/**
	 * Converts a time value from the HTML video element to an integer, e.g. currentTime, duration.
	 * 
	 * @param val
	 * @returns time value as an integer
	 */
	function secondsToInt(val) {
		if ($.isNumeric(val)) {
			return Math.round(val);
		}
		
		return null;
	}
	
	/**
	 * Convert a value in seconds to a time string.
	 * 
	 * @param val
	 * 		time value, in seconds
	 * @returns time value as string, e.g. MM:SS
	 */
	function secondsToTime(val) {
		var sec_num = parseInt(val, 10);
		var hours   = Math.floor(sec_num / 3600);
		var minutes = Math.floor((sec_num - (hours * 3600)) / 60);
		var seconds = sec_num - (hours * 3600) - (minutes * 60);

		//if (hours   < 10) {hours   = "0"+hours;}
		if (minutes < 10) {minutes = "0"+minutes;}
		if (seconds < 10) {seconds = "0"+seconds;}
		
		if (hours > 0)
			return hours + ':' + minutes + ':' + seconds;
		
		return minutes + ':' + seconds;
	}
	
	/**
	 * Toggle the video full screen.
	 * 
	 * @param videoContainerEl
	 * 		the video container element, not the jQuery object
	 */
	function toggleFullScreen(videoContainerEl) {
		if (isFullScreen()) {
			if (document.exitFullscreen) document.exitFullscreen();
			else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
			else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
			else if (document.msExitFullscreen) document.msExitFullscreen();
		}
		else {
			if (videoContainerEl.requestFullscreen) videoContainerEl.requestFullscreen();
			else if (videoContainerEl.webkitRequestFullscreen) videoContainerEl.webkitRequestFullscreen();
			else if (videoContainerEl.mozRequestFullScreen) videoContainerEl.mozRequestFullScreen();
			else if (videoContainerEl.msRequestFullscreen) videoContainerEl.msRequestFullscreen();
		}
	}
	
	/**
	 * Check if video is full screen.
	 * 
	 * @returns true / false
	 */
	function isFullScreen() {
		return !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
	}
	
	/**
	 * Check if full screen is supported by the client.
	 * 
	 * @returns true / false
	 */
	function isFullScreenAvailable() {
		return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled || document.mozFullScreenEnabled || document.msFullscreenEnabled);
	}
	
	/**
	 * Add the book marks or chapter marks to the progress control.
	 * 
	 * @param container
	 * 		the video container jQuery object
	 * @param video
	 * 		the video jQuery object
	 * @param controls
	 * 		the video controls jQuery object
	 * @param markers
	 * 		array of book mark or chapter objects
	 * @param bookmarks
	 * 		true if adding book marks or false if adding chapters
	 */
	function addMarkers(container, video, controls, markers, bookmarks) {
		var duration, pos;
		if (video && video.length)
			duration = video[0].duration;
		if (container && video && controls && markers && duration) {
			var ctrl = controls.find("div.seekControl");
			for (var i=0; i < markers.length; i++) {
				pos = parseInt(markers[i].position / duration * 100);
				if (pos >= 0 && pos < 100) { // make sure marker time is within range
					ctrl.append($("<div/>")
							.addClass(bookmarks ? "bookmark" : "chapter")
							.attr({
								"data-id" : markers[i].id,
								"data-time" : markers[i].position
							})
							.css("left", pos + '%')
							.on("click", function(e) {
								setVideoPosition(container[0], video[0], parseFloat($(this).attr("data-time")));
							})
					)
				}
			}
		}
	}
	
	/**
	 * Delete a single book mark from the progress control.
	 * 
	 * @param ctrl
	 * 		progress control jQuery object
	 * @param bookmark
	 * 		book mark object
	 */
	function delBookmark(ctrl, bookmark) {
		if (ctrl) {
			ctrl.children("div.bookmark").remove("[data-id=" + bookmark.id + "]");
		}
	}
	
	/**
	 * Delete a single chapter from the progress control.
	 * 
	 * @param ctrl
	 * 		progress control jQuery object
	 * @param chapter
	 * 		book mark object
	 */
	function delChapter(ctrl, chapter) {
		if (ctrl) {
			ctrl.children("div.chapter").remove("[data-time=" + chapter.position + "]");
		}
	}
	
	/**
	 * Difference between two arrays, resulting in the objects from the first array
	 * that are not in the second array.
	 * 
	 * @param a1
	 * 		array of either chapter or book mark objects
	 * @param a2
	 * 		array of either chapter or book mark objects
	 * @returns an array of objects from a1 that are not in a2
	 */
	function diff(a1, a2) {
		var result = [], exists;
		//var a3 = a2.map(function(obj) { return obj.id; });
		for (var i=0; i < a1.length; i++) {
			//if (a3.indexOf(a1[i].id) < 0) {
			//	result.push(a1[i]);
			//}
			exists = false;
			for (var j=0; j < a2.length; j++) {
				if (a1[i].id == a2[j].id && a1[i].position == a2[j].position) {
					exists = true;
					break;
				}
			}
			
			if (exists == false) {
				result.push(a1[i]);
			}
		}
		return result;
	}
	
	/**
	 * First, if the web VTT file (thumbnail data) is not passed to the function, retrieve it from
	 * the list stored in the video object as a data attribute, based on the given bit rate.
	 * The web VTT file is then requested from the server and, if successful, the response is parsed
	 * to an array of thumbnail objects.  The array should be ordered chronologically and is used
	 * to display a thumbnail image at any given point of time in the video.
	 * 
     * @param settings
     * 		current settings of player
     * @param video
	 * 		the video jQuery object
	 * @param controls
	 * 		the video controls jQuery object
	 * @param mpd
	 * 		the URL of the DASH manifest file
	 * @param bitrate
	 * 		used to select the Web VTT file if there is more than one (different resolutions)
	 * @param vttUrl
	 * 		URL of the Web VTT file, if available then there is no need to parse the manifest again and we just need to load the VTT file
	 */
	function loadThumbnailData(settings, video, controls, mpd, bitrate, vttUrl) {
		var signedUrls = video.data("signedUrls") || [];
		
		// get the base URL (for the Web VTT file and sprite image files) from the manifest URL
		var ndx, baseUrl = "";
		if ((ndx = mpd.lastIndexOf('/')) >= 0) {
			baseUrl = mpd.substr(0, ndx + 1);
		}
		
		//
		// Get the name of the thumbnail data file (Web VTT) from the list that was read
		// from the DASH manifest and stored in the video object as a data attribute.
		// Also, store the current bandwidth of the selected file so it can be checked if
		// the video resolution changes, which could result in a different thumbnail data file.
		// 
		// @returns the URL of the Web VTT file for the current resolution
		//
		function getDataFileUrl() {
			var dataFiles = video.data("thumbnailFiles") || [],
				dataFileName,
				bandwidth,
				thumbnailData;
			
			for (var i=0; i < dataFiles.length; i++) {
				var dataFile = dataFiles[i];
				
				if (!dataFileName || dataFile.bandwidth <= bitrate) {
					dataFileName = dataFile.url;
					bandwidth = dataFile.bandwidth;
				}
			}
			
			thumbnailData = controls.data("thumbnails") || {};
			thumbnailData.currentBandwidth = bandwidth;
			controls.data("thumbnails", thumbnailData);
			
			return dataFileName;
		}
		
		//
		// Request the Web VTT file from the server as simple text,
		// no pre-processing is done to the return data
		// 
		// @param data
		// 		the URL of the Web VTT file
		//
		function getVTT(data) {
			if (data) {
				var url = signedUrls[data] || data;
				return $.ajax({
					url : url,
					dataType : "text",
					xhrFields: {
						withCredentials: settings.signVideoUrls === false
				    },			
					processData : false
				});
			}
			
			return $.Deferred().reject("getVTT:  URL is undefined");
		}
		
		//
		// Called after a successful response from requesting the Web VTT file from the server.
		// The content of the file is parsed to an array of thumbnail data and
		// stored in a data attribute of the video controls object.
		// 
		// @param data
		// 		the content of the Web VTT file as text
		//
		function success(data) {
			// parse the web VTT file to JSON - an array of thumbnail data
			var result = parseWebVTTtoJSON(data, baseUrl),
				thumbnailData = null;
			if (result.errors.length) {
				for (var i=0; i < result.errors.length; i++) {
					var error = result.errors[i],
					message = error.message;
					if (error.id)
						message += ", id " + error.id;
					$.error("PARSE ERROR Web VTT File: "+message);
				}
			}
			else if (result.thumbnails.length) {
				thumbnailData = controls.data("thumbnails") || {};
				thumbnailData.thumbnails = result.thumbnails;
				// here we add signed URLs for the sprite image files
				if (settings.signVideoUrls === true) {
					var imgUrls = [], url;
					for (var i=0; i < result.thumbnails.length; i++) { // loop through all records, in case there is more than one sprite image file
						url = result.thumbnails[i].url;
						if (imgUrls.indexOf(url) < 0 && !signedUrls[url]) {
							imgUrls.push(url); 
						}
					}
					if (imgUrls.length) {
						// 1. sign the URLs for the sprite images
						// 2. add signed URLs for sprite images to master array of signed URLs
						signUrls(settings, imgUrls)
						.done(function(data) {
							if (data && data.urls) {
								var baseUrl, ndx;
								for (var i=0; i < data.urls.length; i++) {
									baseUrl = data.urls[i].url;
									if ((ndx = baseUrl.lastIndexOf('?')) >= 0) {
										baseUrl = baseUrl.substr(0, ndx);
									}
									signedUrls[baseUrl] = data.urls[i].url;
								}
								video.data("signedUrls", signedUrls);
							}
						})
						.fail(function(jqXHR, textStatus, errorThrown) {
							$.error("FAILURE signing URLs: "+textStatus+"/"+errorThrown+"/"+jqXHR.status+"/"+jqXHR.responseText+"\n\tSprite Urls: "+imgUrls.toString());
						});
					}
				}
			}
			
			controls.data("thumbnails", thumbnailData);
		}
		
		// get and process Web VTT file
		if (vttUrl === undefined) {
			getVTT(getDataFileUrl())
			.done(success)
			.fail(function(jqXHR, textStatus, errorThrown) {
				if ($.type(jqXHR) === "string") {
					$.error("FAILURE loadThumbnailData: "+jqXHR+"\n\tDASH Manifest: "+mpd);
				}
				else {
					$.error("FAILURE loadThumbnailData: "+textStatus+"/"+errorThrown+"/"+jqXHR.status+"/"+jqXHR.responseText+"\n\tDASH Manifest: "+mpd);
				}
			});
		}
		// process Web VTT file only
		else {
			getVTT(vttUrl)
			.done(success)
			.fail(function(jqXHR, textStatus, errorThrown) {
				if ($.type(jqXHR) === "string") {
					$.error("FAILURE loadThumbnailData: "+jqXHR+"\n\tDASH Manifest: "+mpd);
				}
				else {
					$.error("FAILURE loadThumbnailData: "+textStatus+"/"+errorThrown+"/"+jqXHR.status+"/"+jqXHR.responseText+"\n\tDASH Manifest: "+mpd);
				}
			});
		}
	}
	
	/**
	 * Parse a web VTT file to JSON (an array of thumbnail objects).
	 * The thumbnail objects are: {
	 * 		id				- just a simple sequential identifier
	 * 		startTime		- beginning of the range of time this thumbnail image applies to
	 * 		endTime			- ending of the range of time this thumbnail image applies to
	 * 		url				- image sprite file path
	 * 		x				- image index within the sprite on the horizontal plane
	 * 		y				- image index within the sprite on the vertical plane
	 * 		width			- width of the image in pixels
	 * 		height			- height of the image in pixels
	 * }
	 * 
	 * (Adapted from:  https://github.com/annevk/webvtt)
	 * 
	 * @param input
	 * 		web VTT file as a string
	 * @param baseUrl
	 * 		path for the image file
	 * @returns
	 * 		an object with the thumbnail data (as an array) and any errors that might have occurred (as an array)
	 */
    function parseWebVTTtoJSON(input, baseUrl) {
		if (!input.length) return null;
		
		if (baseUrl === undefined)
			baseUrl = "";
		
		var NEWLINE = /\r\n|\r|\n/,
			linePos = 0,
			lines = input.split(NEWLINE),
			thumbnails = [],
			errors = [];
		
		function err(message, id) {
			errors.push({message:message, id:id});
		}
		function toSeconds(t_in) {
			var t = t_in.split( ":" ),
				l = t_in.length,
				time;
			// Invalid time string provided
			if ( l !== 12 && l !== 9 ) {
				throw "Invalid time string provided";
			}
			l = t.length - 1;
			try {
				time = parseInt( t[ l-1 ], 10 ) * 60 + parseFloat( t[ l ], 10 );
				// Hours were given
				if ( l === 2 ) {
					time += parseInt( t[ 0 ], 10 ) * 3600;
				}
			} catch ( e ) {
				throw "Invalid time string provided";
			}
			return time;
		}
		function toPositionAndSize(str) {
			var token = "xywh=",
				values;
			if (str.indexOf(token) !== 0) {
				throw "Invalid position and size string provided";
			}
			values = JSON.parse("[" + str.substr(token.length) + "]");
			if (values.length !== 4) {
				throw "Invalid position and size string provided";
			}
			return values;
		}

		var line = lines.shift(),
			lineLength = line.length,
			signature = "WEBVTT",
			bom = 0,
			signature_length = signature.length;

		/* Byte order mark */
		if (line[0] === "\ufeff") {
			bom = 1
			signature_length += 1
		}
		/* SIGNATURE */
		if (
			lineLength < signature_length ||
			line.indexOf(signature) !== 0+bom ||
			lineLength > signature_length &&
			line[signature_length] !== " " &&
			line[signature_length] !== "\t"
		) {
			err("No valid signature. (File needs to start with \"WEBVTT\".)");
		}

		line = lines.shift();

		/* HEADER */
		if (line != "" && line != undefined) {
			err("No blank line after the signature.");
			return {thumbnails:thumbnails, errors:errors};
		}

		/* BEGIN PAYLOAD LOOP */
		var count = 0;
		var thumbnail = null;
		while(lines.length) {
			line = lines.shift();
			
			if (line == "" || line == undefined) {
				if (thumbnail) {
					thumbnails.push(thumbnail);
				}
				thumbnail = null;
				continue;
			}

			if (!thumbnail) {
				thumbnail = {
					id : ++count,
					startTime : NaN,
					endTime : NaN,
					url : "",
					x : NaN,
					y : NaN,
					width : NaN,
					height : NaN
				};
			}

			/* TIMINGS */
			if (line.indexOf( "-->" ) > 0) {
				var lineSegments = line.replace( /-->/, " --> " ).split( /[\t ]+/ );
				
				if ( lineSegments.length < 3 ) {
					err("Missing time stamp. \"" + line + "\"", thumbnail.id);
				}
				else {
					try {
						thumbnail.startTime = toSeconds( lineSegments[ 0 ] );
						thumbnail.endTime = toSeconds( lineSegments[ 2 ] );
					} catch ( e ) {
						err("Invalid time stamp. \"" + line + "\"", thumbnail.id);
					}
				}
			}
			else if (line.indexOf( "#" ) > 0) {
				var lineSegments = line.replace( /#/, " # " ).split( /[\t ]+/ );
				
				if ( lineSegments.length < 3 ) {
					err("Incorrect thumbnail string. \"" + line + "\"", thumbnail.id);
				}
				else {
					try {
						// image file path
						thumbnail.url = baseUrl + lineSegments[ 0 ];
						// position and size
						var pas = toPositionAndSize( lineSegments[ 2 ] );
						thumbnail.x = pas[0]; // x position
						thumbnail.y = pas[1]; // y position
						thumbnail.width = pas[2]; // width
						thumbnail.height = pas[3]; // height
					} catch ( e ) {
						err("Incorrect thumbnail string. \"" + line + "\"", thumbnail.id);
					}
				}
			}
			else {
				err("Invalid line. \"" + line + "\"", thumbnail.id);
			}
		}
		/* END PAYLOAD LOOP */
		
		return { thumbnails:thumbnails, errors:errors };
    }
	
    /**
     * Get the thumbnail object for a given time value.
	 * The thumbnail object is: {
	 * 		id				- just a simple sequential identifier
	 * 		startTime		- beginning of the range of time this thumbnail image applies to
	 * 		endTime			- ending of the range of time this thumbnail image applies to
	 * 		url				- image sprite file path
	 * 		x				- image index within the sprite on the horizontal plane
	 * 		y				- image index within the sprite on the vertical plane
	 * 		width			- width of the image in pixels
	 * 		height			- height of the image in pixels
	 * }
     * 
     * @param thumbnails
     * 		array of thumbnail data
     * @param time
     * 		time value in seconds
     * @returns
     * 		a thumbnail object
     */
	function getThumbnail(thumbnails, time) {
		var search = function() {
			var low = 0;
			var high = thumbnails.length - 1;
			var mid, guess;
			
			while ( low <= high ) {
				mid = Math.floor((low + high) / 2);
				guess = thumbnails[mid];

				if ( guess.startTime > time ) {
					high = mid - 1;
				} else if ( guess.endTime <= time ) {
					low = mid + 1;
				} else {
					return mid;
				}
			}

			return -1;
		};
		
		var thumbnail = thumbnails[thumbnails.length - 1], ndx;
		if ((ndx = search()) >= 0) {
			thumbnail = thumbnails[ndx];
		}
		
		return thumbnail;
	}
	
	/**
	 * Request the DASH manifest from the server as an XML document.
	 * 
	 * @param mpd
	 * 		the URL of the DASH manifest file
	 * @returns the return data is pre-processed using $.parseXML
	 */
	function getDashManifest(settings, mpd) {
		if (mpd) {
			return $.ajax({
				url : mpd,
				dataType : "xml",
				xhrFields: {
					withCredentials: settings.signVideoUrls === false
			    },			
				processData : false
			});
		}
		
		return $.Deferred().reject("getDashManifest:  URL is undefined");
	}
	
	/**
	 * Call web service to sign one or more URLs.
	 * 
	 * @param settings
	 * 		the video player settings
	 * @param data
	 * 		an array of URLs to be signed
	 * @returns a JSON object with the requested signed URLs
	 */
	function signUrls(settings, data) {
		var obj = new Object();
		obj.action = "signurls";
		obj.urls = [];
		
		if ($.isArray(data)) {
			for (var i=0; i < data.length; i++) {
				var url = new Object();
				url.url = data[i];
				obj.urls.push(url);
			}
		}
		
		var jsonString = JSON.stringify(obj);
		
		return $.ajax({
			url : settings.serviceUrl,
			data : jsonString,
			contentType : "application/json",
			cache : false,
			type : "POST"
		});
	}
	
	/**
	 * Send an info mail when video player errors occur.
	 * 
	 * @param settings
	 * 		the video player settings
	 * @param src
	 * 		the video source
	 * @param tech
	 * 		from which technology did the error come from:  DASH or HTML5
	 * @param err
	 * 		error event object
	 */
	function reportError(settings, src, tech, err) {
		if (settings.reportErrors) {
			var jsonString,
			obj = new Object();
			
			obj.action = "errVideoPlayer";
			obj.vid = settings.data.videoID;
			obj.uid = settings.data.userDataID;
			obj.cid = settings.data.classroomID;
			obj.oid = settings.data.organisationID;
			obj.src = src;
			obj.tech = tech;
			obj.data = JSON.stringify(err);
			
			jsonString = JSON.stringify(obj);
			
			$.ajax({
				url : settings.serviceUrl,
				data : jsonString,
				contentType : "application/json",
				cache : false,
				type : "POST"
			});
		}
	}

}( jQuery, window, document ));
